import { z } from 'zod';
/**
 * Contratos compartidos entre `apps/api` y `apps/web`.
 *
 * Regla del proyecto: una operacion tiene UN solo esquema. El servidor valida la
 * entrada con el, el cliente construye el formulario con el, y los tipos salen
 * de el. Nada de definir la misma forma dos veces.
 */
// --- Comunes ---------------------------------------------------------------
/*
  CAPTADOR es un oficio, no un manager con menos permisos. Sale a la calle,
  registra puertas y obras, y no ve ni casas, ni dinero, ni clientes.
*/
export const ROLES = [
    'DIRECTOR',
    'MANAGER',
    'PROVEEDOR',
    'CLIENTE',
    'CAPTADOR',
    /*
      PROMOTOR mira y no toca. No es un cliente con menos permisos: es alguien de
      fuera con interes legitimo en como va una casa —la construyo, la financia,
      la comparte— y ninguno en gestionarla. Ve las que se le asignan, y en ellas
      no hay un solo boton que escriba.
    */
    'PROMOTOR',
];
export const rolSchema = z.enum(ROLES);
export const IDIOMAS = ['es', 'en', 'de'];
export const idiomaSchema = z.enum(IDIOMAS);
/** Todo importe viaja con su moneda. Nunca un numero suelto. */
export const importeSchema = z.object({
    cents: z.number().int(),
    moneda: z
        .string()
        .length(3)
        .regex(/^[A-Z]{3}$/),
});
const paisSchema = z
    .string()
    .length(2)
    .regex(/^[A-Za-z]{2}$/)
    .transform((v) => v.toUpperCase());
const motivoSchema = z.string().min(3).max(500);
// --- Autenticacion ---------------------------------------------------------
export const loginRequestSchema = z.object({
    email: z.string().email().max(254),
    password: z.string().min(8).max(200),
    // Segundo factor, cuando el usuario lo tiene activo. Seis dígitos; opcional en
    // el primer intento, que es el que devuelve `requiere2fa`.
    codigo2fa: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
});
/** Alta del segundo factor: el secreto se muestra UNA vez para el QR. */
export const inicio2faSchema = z.object({
    secret: z.string(),
    uri: z.string(),
});
export const confirmar2faSchema = z.object({
    codigo: z.string().regex(/^\d{6}$/),
});
export const estado2faSchema = z.object({ activo: z.boolean() });
export const usuarioSesionSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    tenantNombre: z.string(),
    email: z.string().email(),
    nombre: z.string(),
    rol: rolSchema,
    idioma: idiomaSchema,
    /**
     * Si esta persona entra en el CRM. Direccion siempre; al resto se lo da y se
     * lo quita la direccion desde «Usuarios». Viaja en la sesion porque decide
     * que entradas del menu se ensenan; quien decide de verdad es el servidor.
     */
    crmHabilitado: z.boolean(),
});
// --- Personas y empresas ---------------------------------------------------
export const TIPOS_PARTY = [
    'PERSONA_FISICA',
    'EMPRESA',
    'COMUNIDAD_PROPIETARIOS',
    'OTRA_ENTIDAD_JURIDICA',
];
export const tipoPartySchema = z.enum(TIPOS_PARTY);
export const partySchema = z.object({
    id: z.string().uuid(),
    tipo: tipoPartySchema,
    nombre: z.string(),
    pais: z.string().length(2),
    nif: z.string().nullable(),
    email: z.string().nullable(),
    telefono: z.string().nullable(),
    direccionFacturacion: z.string().nullable(),
    idioma: idiomaSchema,
    estado: z.enum(['ACTIVA', 'ARCHIVADA']),
});
/**
 * Alta de party. Solo exige tipo, nombre y pais.
 *
 * Los datos de facturacion NO se exigen aqui: se exigen cuando la party actua
 * como cliente de facturacion de un contrato. Un propietario no necesita tener
 * el NIF cargado para que podamos registrar que es propietario.
 */
export const crearPartySchema = z.object({
    tipo: tipoPartySchema,
    nombre: z.string().min(2).max(200),
    pais: paisSchema,
    nif: z.string().min(3).max(40).optional(),
    email: z.string().email().max(254).optional(),
    telefono: z.string().max(40).optional(),
    direccionFacturacion: z.string().max(500).optional(),
    idioma: idiomaSchema.optional(),
    notasInternas: z.string().max(2000).optional(),
});
export const editarPartySchema = crearPartySchema.partial().omit({ tipo: true });
/** Datos que una party debe tener para poder ser cliente de facturacion. */
export const DATOS_FACTURACION_REQUERIDOS = ['nif', 'email', 'direccionFacturacion'];
// --- Relaciones propiedad-party --------------------------------------------
export const ROLES_RELACION = ['OWNER', 'PROMOTER', 'ASSET_MANAGER', 'AUTHORIZED_CONTACT'];
export const rolRelacionSchema = z.enum(ROLES_RELACION);
/** Roles de los que solo puede haber uno vigente por propiedad. */
export const ROLES_EXCLUSIVOS = ['PROMOTER', 'ASSET_MANAGER'];
export const relacionSchema = z.object({
    id: z.string().uuid(),
    partyId: z.string().uuid(),
    partyNombre: z.string(),
    rol: rolRelacionSchema,
    desde: z.string(),
    hasta: z.string().nullable(),
    motivoFin: z.string().nullable(),
});
export const crearRelacionSchema = z.object({
    partyId: z.string().uuid(),
    rol: rolRelacionSchema,
    desde: z.string().date().optional(),
});
export const finalizarRelacionSchema = z.object({
    motivo: motivoSchema,
    hasta: z.string().date().optional(),
});
// --- Propiedades -----------------------------------------------------------
/**
 * Estados de propiedad. Describen el CICLO DE VIDA, no la integridad de sus
 * relaciones: que falte el contrato o el propietario es una configuracion
 * incompleta que se senala aparte, no la identidad de la villa.
 */
export const ESTADOS_PROPIEDAD = [
    'ONBOARDING',
    'ACTIVE',
    'SUSPENDED',
    'INTERNAL_DEMO',
    'ARCHIVED',
];
export const estadoPropiedadSchema = z.enum(ESTADOS_PROPIEDAD);
export const TIPOS_PROPIEDAD = [
    'VIVIENDA',
    'VILLA',
    'APARTAMENTO',
    'FINCA',
    'LOCAL',
    'OTRO',
];
export const tipoPropiedadSchema = z.enum(TIPOS_PROPIEDAD);
export const SEMAFOROS = ['VERDE', 'AMARILLO', 'ROJO'];
export const semaforoSchema = z.enum(SEMAFOROS);
export const nivelNioSchema = z.number().int().min(1).max(5);
export const propiedadResumenSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    zona: z.string().nullable(),
    pais: z.string(),
    tipoPropiedad: tipoPropiedadSchema,
    estado: estadoPropiedadSchema,
    nivelNio: nivelNioSchema.nullable(),
    semaforo: semaforoSchema,
    propietarios: z.array(z.object({ id: z.string().uuid(), nombre: z.string() })),
    requisitosPendientes: z.number().int().min(0),
    /** Incidencias abiertas de la propiedad. Alimenta el conteo de la tarjeta. */
    incidenciasAbiertas: z.number().int().min(0),
    /**
     * La foto de portada, si la casa tiene una. Nula mientras no la tenga, y
     * entonces la tarjeta pinta su ilustracion marcada como ficticia.
     */
    fotoPortadaUrl: z.string().nullable(),
    /**
     * Que niveles hay entre las incidencias activas, sin repetir. La tarjeta los
     * pinta como pastillas: de un vistazo se ve si lo que hay son cuatro mejoras
     * esteticas o una urgencia.
     */
    nivelesAbiertos: z.array(z.number().int()),
    /**
     * Responsable interno. Solo se envia a roles internos: es organizacion de la
     * empresa gestora, no informacion que corresponda a un propietario.
     */
    responsableInternoUserId: z.string().uuid().nullable().optional(),
});
export const crearPropiedadSchema = z.object({
    codigo: z.string().min(1).max(40),
    nombre: z.string().min(2).max(200),
    pais: paisSchema,
    zonaHoraria: z.string().min(3).max(60),
    tipoPropiedad: tipoPropiedadSchema,
    direccion: z.string().max(300).optional(),
    zona: z.string().max(120).optional(),
});
export const editarPropiedadSchema = z.object({
    nombre: z.string().min(2).max(200).optional(),
    direccion: z.string().max(300).optional(),
    zona: z.string().max(120).optional(),
    tipoPropiedad: tipoPropiedadSchema.optional(),
    zonaHoraria: z.string().min(3).max(60).optional(),
    referenciaCatastral: z.string().max(40).optional(),
    metros: z.number().positive().max(1_000_000).optional(),
    responsableInternoUserId: z.string().uuid().nullable().optional(),
});
export const cambiarEstadoPropiedadSchema = z.object({
    estado: estadoPropiedadSchema,
    motivo: motivoSchema.optional(),
});
/** Lo que falta para poder activar una propiedad. */
export const CODIGOS_REQUISITO = [
    'DIRECCION',
    'ZONA',
    'PROPIETARIO',
    'CONTRATO',
    'CLIENTE_FACTURACION',
    'TARIFA',
    'NIO_VALIDADA',
    'RESPONSABLE_INTERNO',
];
export const codigoRequisitoSchema = z.enum(CODIGOS_REQUISITO);
export const requisitosActivacionSchema = z.object({
    cumplidos: z.array(codigoRequisitoSchema),
    pendientes: z.array(codigoRequisitoSchema),
    puedeActivarse: z.boolean(),
});
// --- Contrato de gestion ---------------------------------------------------
export const ESTADOS_CONTRATO = ['DRAFT', 'ACTIVE', 'TERMINATED'];
export const estadoContratoSchema = z.enum(ESTADOS_CONTRATO);
export const MODALIDADES_TARIFA = ['MANUAL', 'NIO'];
export const modalidadTarifaSchema = z.enum(MODALIDADES_TARIFA);
export const contratoSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    propertyId: z.string().uuid(),
    estado: estadoContratoSchema,
    desde: z.string(),
    hasta: z.string().nullable(),
    motivoFin: z.string().nullable(),
    moneda: z.string().length(3),
    umbralAprobacion: importeSchema,
    modalidadTarifa: modalidadTarifaSchema,
    clienteFacturacion: z.object({ id: z.string().uuid(), nombre: z.string() }).nullable(),
    tarifaVigente: importeSchema.nullable(),
    nioEvaluationId: z.string().uuid().nullable(),
});
export const crearContratoSchema = z.object({
    billingPartyId: z.string().uuid(),
    desde: z.string().date().optional(),
    moneda: z.string().length(3).default('EUR'),
    umbralAprobacionCents: z.number().int().min(0).default(50_000),
    referenciaDocumental: z.string().max(300).optional(),
    notas: z.string().max(2000).optional(),
});
/**
 * Fijar tarifa. Union discriminada: el TIPO obliga a aportar la evaluacion
 * cuando el origen es NIO, de modo que no se puede compilar una llamada
 * incoherente. Una tarifa por NIO que no dijera de que evaluacion sale no
 * seria reproducible manana.
 */
export const fijarTarifaSchema = z.discriminatedUnion('origen', [
    z.object({
        origen: z.literal('MANUAL'),
        importeCents: z.number().int().min(0),
        moneda: z.string().length(3).optional(),
    }),
    z.object({
        origen: z.literal('NIO'),
        nioEvaluationId: z.string().uuid(),
        /** Si se omite, se toma la cuota sugerida de la tabla base de esa evaluacion. */
        importeCents: z.number().int().min(0).optional(),
        moneda: z.string().length(3).optional(),
    }),
]);
/**
 * Finalizar exige las dos cosas: fecha efectiva y motivo. Ninguna es opcional,
 * porque un contrato sin fecha de fin no se puede liquidar, y uno sin motivo no
 * se puede explicar seis meses despues.
 */
export const finalizarContratoSchema = z.object({
    motivo: motivoSchema,
    hasta: z.string().date(),
});
// --- NIO -------------------------------------------------------------------
export const FACTORES_NIO = [
    'valorActivo',
    'complejidadTecnica',
    'frecuenciaUso',
    'exigenciaCliente',
    'exposicionClimatica',
    'historialIntervencion',
];
const puntuacionFactor = z.number().int().min(1).max(5);
export const evaluacionNioRequestSchema = z.object({
    valorActivo: puntuacionFactor,
    complejidadTecnica: puntuacionFactor,
    frecuenciaUso: puntuacionFactor,
    exigenciaCliente: puntuacionFactor,
    exposicionClimatica: puntuacionFactor,
    historialIntervencion: puntuacionFactor,
    notas: z.string().max(2000).optional(),
});
/**
 * Calculo puro del NIO, compartido por servidor e interfaz.
 *
 * Vive aqui, en los contratos, y no duplicado en cada lado: la pantalla necesita
 * mostrar el total y el nivel en vivo mientras se rellenan los seis factores, y
 * tener dos implementaciones de una regla inmutable es la forma mas segura de
 * que un dia dejen de coincidir. El servidor valida y decide; la interfaz solo
 * previsualiza con la MISMA formula.
 */
export const CORTES_NIO = [
    { hasta: 10, nivel: 1 },
    { hasta: 15, nivel: 2 },
    { hasta: 20, nivel: 3 },
    { hasta: 25, nivel: 4 },
];
export function nivelDesdeTotalNio(total) {
    for (const corte of CORTES_NIO) {
        if (total <= corte.hasta)
            return corte.nivel;
    }
    return 5;
}
/** Suma los seis factores. No valida: eso es competencia del servidor. */
export function totalNio(puntuaciones) {
    return FACTORES_NIO.reduce((suma, factor) => suma + (puntuaciones[factor] ?? 0), 0);
}
export const ESTADOS_EVALUACION_NIO = ['BORRADOR', 'VALIDADA', 'SUSTITUIDA'];
export const estadoEvaluacionNioSchema = z.enum(ESTADOS_EVALUACION_NIO);
/**
 * Vista COMPLETA de una evaluacion. Solo para Direccion: incluye las
 * puntuaciones por factor, el total, la version de tabla y la cuota sugerida.
 */
export const evaluacionNioCompletaSchema = z.object({
    id: z.string().uuid(),
    propertyId: z.string().uuid(),
    estado: estadoEvaluacionNioSchema,
    valorActivo: z.number().int(),
    complejidadTecnica: z.number().int(),
    frecuenciaUso: z.number().int(),
    exigenciaCliente: z.number().int(),
    exposicionClimatica: z.number().int(),
    historialIntervencion: z.number().int(),
    total: z.number().int(),
    nivel: nivelNioSchema,
    rateTableVersion: z.number().int(),
    cuotaSugerida: importeSchema.nullable(),
    creadoEn: z.string(),
});
/**
 * Vista REDUCIDA. Es lo unico que recibe un manager: el nivel y nada mas.
 * Cliente, propietario y promotora no reciben ni esto.
 */
export const evaluacionNioReducidaSchema = z.object({
    id: z.string().uuid(),
    propertyId: z.string().uuid(),
    estado: estadoEvaluacionNioSchema,
    nivel: nivelNioSchema,
    creadoEn: z.string(),
});
export const tablaNioSchema = z.object({
    id: z.string().uuid(),
    version: z.number().int(),
    vigenteDesde: z.string(),
    moneda: z.string().length(3),
    nivel1Cents: z.number().int(),
    nivel2Cents: z.number().int(),
    nivel3Cents: z.number().int(),
    nivel4Cents: z.number().int(),
    nivel5Cents: z.number().int(),
});
export const crearTablaNioSchema = z.object({
    vigenteDesde: z.string().date(),
    moneda: z.string().length(3).default('EUR'),
    nivel1Cents: z.number().int().min(0),
    nivel2Cents: z.number().int().min(0),
    nivel3Cents: z.number().int().min(0),
    nivel4Cents: z.number().int().min(0),
    nivel5Cents: z.number().int().min(0),
    notas: z.string().max(2000).optional(),
});
// --- Incidencias -----------------------------------------------------------
/*
 * Abierta, en curso, cerrada y anulada.
 *
 * «En curso» SIGUE SIENDO ACTIVA: cuenta para el semaforo de la casa, para las
 * alertas y para el conteo de incidencias activas, igual que una abierta. Una
 * incidencia deja de pesar cuando se resuelve, no cuando alguien empieza a
 * mirarla.
 */
export const ESTADOS_INCIDENCIA = ['OPEN', 'IN_PROGRESS', 'CLOSED', 'CANCELLED'];
export const estadoIncidenciaSchema = z.enum(ESTADOS_INCIDENCIA);
export const nivelIncidenciaSchema = z.number().int().min(1).max(4);
export const areaIncidenciaSchema = z.enum([
    'INTERIOR', 'EXTERIOR', 'SISTEMAS', 'PISCINA', 'JARDIN', 'GESTION', 'OTRO',
]);
export const incidenciaSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    propertyId: z.string().uuid(),
    titulo: z.string(),
    descripcion: z.string().nullable(),
    nivel: nivelIncidenciaSchema,
    estado: estadoIncidenciaSchema,
    area: areaIncidenciaSchema,
    costeEstimadoCents: z.number().int().nullable(),
    costeFinalCents: z.number().int().nullable(),
    slaHoras: z.number().int().nullable(),
    /**
     * Horas transcurridas desde la apertura. Se calcula al leer, no se guarda:
     * una bandera guardada se quedaria obsoleta en cuanto pasara el tiempo.
     */
    horasTranscurridas: z.number().int(),
    slaIncumplido: z.boolean(),
    proveedorNombre: z.string().nullable(),
    /** Primer adjunto de imagen, para la miniatura de la lista. */
    portadaAdjuntoId: z.string().uuid().nullable(),
    visibleCliente: z.boolean(),
    resolucion: z.string().nullable(),
    /**
     * La fecha que el equipo se pone para tenerla resuelta. NO es el SLA: el SLA
     * dice cuantas horas deberia tardar segun su nivel, y sirve para medir; esta
     * es una fecha, y sirve para planificar y para salir en «Tareas del dia» el
     * dia que toca. Opcional: la mayoria no tiene ninguna prometida.
     */
    fechaEsperada: z.string().nullable(),
    /**
     * Quien del equipo se encarga: la persigue, llama al proveedor y la cierra.
     * Una sola persona; le sale en su «Tareas del dia».
     */
    responsableId: z.string().uuid().nullable(),
    responsableNombre: z.string().nullable(),
    creadoEn: z.string(),
    cerradoEn: z.string().nullable(),
});
export const crearIncidenciaSchema = z.object({
    titulo: z.string().min(3).max(300),
    nivel: nivelIncidenciaSchema,
    descripcion: z.string().max(4000).optional(),
    area: areaIncidenciaSchema.optional(),
    /** En centimos, como todo el dinero del sistema. */
    costeEstimadoCents: z.number().int().min(0).max(100_000_000).optional(),
    visibleCliente: z.boolean().optional(),
});
export const editarIncidenciaSchema = z.object({
    titulo: z.string().min(3).max(300).optional(),
    /** `null` la deja vacia: borrar una descripcion que ya no vale es valido. */
    descripcion: z.string().max(4000).nullable().optional(),
    nivel: nivelIncidenciaSchema.optional(),
    visibleCliente: z.boolean().optional(),
    /** `null` la quita: dejar de prometer una fecha es una decision valida. */
    fechaEsperada: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
});
export const cerrarIncidenciaSchema = z.object({
    resolucion: z.string().min(3).max(2000),
    cerradoEn: z.string().datetime().optional(),
});
export const anularIncidenciaSchema = z.object({ motivo: motivoSchema });
export const reabrirIncidenciaSchema = z.object({ motivo: motivoSchema });
// --- Historial funcional ---------------------------------------------------
export const eventoHistorialSchema = z.object({
    id: z.string().uuid(),
    tipo: z.string(),
    titulo: z.string(),
    detalle: z.string().nullable(),
    ocurridoEn: z.string(),
});
// --- Errores ---------------------------------------------------------------
export const errorRespuestaSchema = z.object({
    error: z.string(),
    mensajeClave: z.string(),
    mensaje: z.string().optional(),
    detalle: z.unknown().optional(),
});
// --- CRM: embudos, etapas, oportunidades y actividades ---------------------
export const crmArquetipoSchema = z.enum(['MANAGEMENT', 'PROMOTORAS', 'VENTA', 'COMPRA', 'CUSTOM']);
export const crmEstadoOportunidadSchema = z.enum(['ABIERTA', 'GANADA', 'PERDIDA']);
export const crmTipoActividadSchema = z.enum(['LLAMADA', 'REUNION', 'EMAIL', 'TAREA', 'NOTA']);
export const crmStageSchema = z.object({
    id: z.string().uuid(),
    pipelineId: z.string().uuid(),
    nombre: z.string(),
    orden: z.number().int(),
    probabilidad: z.number(),
    color: z.string().nullable(),
    mostrarInformes: z.boolean(),
    esGanada: z.boolean(),
    esPerdida: z.boolean(),
});
export const crmPipelineSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    arquetipo: crmArquetipoSchema,
    orden: z.number().int(),
    probPorOportunidad: z.boolean(),
    etapas: z.array(crmStageSchema),
});
/*
  LOS CAMPOS DE LA FICHA, LOS MISMOS QUE EN VENTRY.

  Cualificación (encaje cultural, idioma, uso real, riesgos) y propiedad (zona,
  superficie, tipo, metros, dormitorios, baños, vistas, máximo propuesto). Todo
  texto: así llegan de Ventry —«Villa/Chalet; Finca», «150 – 300 m²», «1.25»— y
  convertirlos a números perdería lo que el equipo escribió.
*/
const campoFicha = z.string().max(300).nullable().optional();
export const detallesOportunidadSchema = z.object({
    encajeCultural: campoFicha,
    idioma: campoFicha,
    usoReal: campoFicha,
    riesgos: z.string().max(2000).nullable().optional(),
    zona: campoFicha,
    superficie: campoFicha,
    tipoPropiedad: campoFicha,
    m2Solar: campoFicha,
    m2Habitables: campoFicha,
    dormitorios: campoFicha,
    banos: campoFicha,
    vistas: campoFicha,
    propuestoMax: campoFicha,
    estaEnLaIsla: campoFicha,
    viveEnLaIsla: campoFicha,
});
/** Otra persona de la misma oportunidad: el socio, la secretaria, el jefe de obra. */
export const contactoAdicionalSchema = z.object({
    nombre: z.string().min(1).max(200),
    rol: z.string().max(120).nullable().optional(),
    email: z.string().max(200).nullable().optional(),
    telefono: z.string().max(60).nullable().optional(),
});
export const crmOpportunitySchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    pipelineId: z.string().uuid(),
    stageId: z.string().uuid(),
    nombre: z.string(),
    partyId: z.string().uuid().nullable(),
    propertyId: z.string().uuid().nullable(),
    valorCents: z.number().int().nullable(),
    moneda: z.string(),
    probManual: z.number().nullable(),
    probabilidad: z.number(),
    estado: crmEstadoOportunidadSchema,
    motivoPerdida: z.string().nullable(),
    responsableUserId: z.string().uuid().nullable(),
    contactoNombre: z.string().nullable(),
    contactoEmail: z.string().nullable(),
    contactoTelefono: z.string().nullable(),
    contactoEmpresa: z.string().nullable(),
    esperadoEn: z.string().nullable(),
    creadoEn: z.string(),
    actualizadoEn: z.string(),
    cerradoEn: z.string().nullable(),
    /* De donde viene. Vacio en todo lo que nace aqui; lleno en lo importado. */
    origen: z.string().nullable(),
    ownerOriginal: z.string().nullable(),
    externalFuente: z.string().nullable(),
    externalId: z.string().nullable(),
    etiquetas: z.array(z.string()),
    /** La cualificacion que traia del sistema de origen, tal cual venia. */
    datosExternos: z.record(z.string()),
    /**
     * Quien la lleva EN EXCLUSIVA: el resto del equipo no contacta. No es el
     * responsable —que se reasigna—, es una prohibicion para todos los demas.
     */
    exclusivoUserId: z.string().uuid().nullable(),
    /**
     * Su nombre, en la propia oportunidad: quien vende tiene que verlo en la
     * tarjeta, y la lista de usuarios es solo de la direccion.
     */
    exclusivoNombre: z.string().nullable(),
    detalles: detallesOportunidadSchema,
    contactosAdicionales: z.array(contactoAdicionalSchema),
    /** Quién más del equipo la sigue. */
    seguidores: z.array(z.string().uuid()),
    responsableNombre: z.string().nullable(),
    creadoPorNombre: z.string().nullable(),
});
export const crmActivitySchema = z.object({
    id: z.string().uuid(),
    opportunityId: z.string().uuid(),
    tipo: crmTipoActividadSchema,
    titulo: z.string(),
    detalle: z.string().nullable(),
    vence: z.string().nullable(),
    hecha: z.boolean(),
    responsableUserId: z.string().uuid().nullable(),
    creadoEn: z.string(),
    /** Quién la escribió: «Creado por» en la nota. */
    autorNombre: z.string().nullable(),
    responsableNombre: z.string().nullable(),
});
export const crearOportunidadSchema = z.object({
    pipelineId: z.string().uuid(),
    stageId: z.string().uuid().optional(),
    nombre: z.string().min(2).max(300),
    partyId: z.string().uuid().optional(),
    propertyId: z.string().uuid().optional(),
    valorCents: z.number().int().min(0).optional(),
    moneda: z.string().length(3).optional(),
    contactoNombre: z.string().max(200).optional(),
    contactoEmail: z.string().email().max(200).optional(),
    contactoTelefono: z.string().max(60).optional(),
    responsableUserId: z.string().uuid().optional(),
    esperadoEn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export const editarOportunidadSchema = z.object({
    nombre: z.string().min(2).max(300).optional(),
    valorCents: z.number().int().min(0).nullable().optional(),
    probManual: z.number().min(0).max(100).nullable().optional(),
    responsableUserId: z.string().uuid().nullable().optional(),
    partyId: z.string().uuid().nullable().optional(),
    propertyId: z.string().uuid().nullable().optional(),
    contactoNombre: z.string().max(200).nullable().optional(),
    /*
      Sin exigir formato de correo: lo importado de Ventry trae «a@x.com · b@x.com»
      en el mismo campo, y una ficha que no se puede guardar porque otro campo vino
      así es una ficha que deja de actualizarse.
    */
    contactoEmail: z.string().max(200).nullable().optional(),
    contactoTelefono: z.string().max(60).nullable().optional(),
    esperadoEn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    exclusivoUserId: z.string().uuid().nullable().optional(),
    /** Embudo y etapa: si cambia el embudo tiene que venir también la etapa. */
    pipelineId: z.string().uuid().optional(),
    stageId: z.string().uuid().optional(),
    estado: crmEstadoOportunidadSchema.optional(),
    motivoPerdida: z.string().max(500).nullable().optional(),
    origen: z.string().max(300).nullable().optional(),
    contactoEmpresa: z.string().max(200).nullable().optional(),
    etiquetas: z.array(z.string().min(1).max(80)).max(60).optional(),
    detalles: detallesOportunidadSchema.optional(),
    contactosAdicionales: z.array(contactoAdicionalSchema).max(10).optional(),
    seguidores: z.array(z.string().uuid()).max(30).optional(),
});
export const moverOportunidadSchema = z.object({
    stageId: z.string().uuid(),
});
export const cerrarOportunidadSchema = z.object({
    resultado: z.enum(['GANADA', 'PERDIDA']),
    motivo: z.string().max(500).optional(),
});
export const crearActividadSchema = z.object({
    tipo: crmTipoActividadSchema,
    titulo: z.string().min(2).max(300),
    detalle: z.string().max(2000).optional(),
    vence: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    responsableUserId: z.string().uuid().optional(),
});
/*
  EL ASISTENTE DEL CRM

  Se le escribe como se escribia en el chat de ChatGPT —«hable con Pouline, esta
  analizando nuestra oferta»— o se le pasa la captura de una conversacion. El
  devuelve una PROPUESTA: que oportunidad es, que nota anadir, si cambia de
  columna y si hay un siguiente paso. No toca nada: lo aplica una persona, con
  un toque, despues de leerlo. Cuando no esta claro de quien se habla, pregunta.
*/
const fechaAsistenteSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const imagenAsistenteSchema = z.object({
    tipoMime: z.enum(['image/jpeg', 'image/png', 'image/webp', 'image/gif']),
    contenidoBase64: z.string().min(1),
});
/** Un turno de la conversacion en curso: lo dicho antes de este mensaje. */
export const turnoAsistenteSchema = z.object({
    rol: z.enum(['PERSONA', 'ASISTENTE']),
    texto: z.string().max(8000),
});
export const interpretarAsistenteSchema = z
    .object({
    texto: z.string().max(8000),
    imagenes: z.array(imagenAsistenteSchema).max(6).default([]),
    historial: z.array(turnoAsistenteSchema).max(20).default([]),
})
    .refine((d) => d.texto.trim().length > 0 || d.imagenes.length > 0, {
    message: 'Hace falta un texto o una captura',
});
export const confianzaAsistenteSchema = z.enum(['ALTA', 'MEDIA', 'BAJA']);
/** Lo que el asistente propone hacer con UNA oportunidad. */
export const accionPropuestaSchema = z.object({
    oportunidadId: z.string().uuid(),
    oportunidadNombre: z.string(),
    embudoNombre: z.string(),
    etapaActualNombre: z.string(),
    estadoActual: crmEstadoOportunidadSchema,
    confianza: confianzaAsistenteSchema,
    /** Por que cree que es ESTA y no otra. Se ensena para poder corregirle. */
    porque: z.string(),
    nota: z.string().nullable(),
    nuevaEtapaId: z.string().uuid().nullable(),
    nuevaEtapaNombre: z.string().nullable(),
    /** Si la nueva etapa cierra la oportunidad: ganada o perdida. */
    nuevaEtapaCierra: z.enum(['GANADA', 'PERDIDA']).nullable(),
    motivoEtapa: z.string().nullable(),
    tarea: z.object({ titulo: z.string(), vence: fechaAsistenteSchema.nullable() }).nullable(),
    avisos: z.array(z.string()),
});
/*
  UNA OPORTUNIDAD QUE TODAVÍA NO EXISTE.

  «Me llamó Hans Weber, tiene una villa en Bendinat y busca quien se la
  gestione»: eso no actualiza nada, da de alta a alguien. El asistente propone
  el alta —embudo, etapa, contacto, la nota y el siguiente paso— y, como con
  lo demás, la crea una persona al aplicar. Si en la cartera hay alguien que
  se le parece, lo dice: un cliente dado de alta dos veces se trabaja dos veces.
*/
export const nuevaPropuestaSchema = z.object({
    /** Identifica la propuesta en pantalla; no es un identificador de la base. */
    clave: z.string(),
    nombre: z.string(),
    pipelineId: z.string().uuid(),
    embudoNombre: z.string(),
    stageId: z.string().uuid(),
    etapaNombre: z.string(),
    etapaCierra: z.enum(['GANADA', 'PERDIDA']).nullable(),
    contactoNombre: z.string().nullable(),
    contactoEmail: z.string().nullable(),
    contactoTelefono: z.string().nullable(),
    contactoEmpresa: z.string().nullable(),
    origen: z.string().nullable(),
    valorCents: z.number().int().nullable(),
    detalles: detallesOportunidadSchema,
    nota: z.string().nullable(),
    tarea: z.object({ titulo: z.string(), vence: fechaAsistenteSchema.nullable() }).nullable(),
    confianza: confianzaAsistenteSchema,
    porque: z.string(),
    avisos: z.array(z.string()),
    /** Las que ya están en la cartera y se le parecen. */
    parecidas: z.array(z.object({ id: z.string().uuid(), nombre: z.string() })),
});
export const propuestaAsistenteSchema = z.object({
    /** PROPUESTA: hay cambios. PREGUNTA: necesita saber algo. NADA: no va de leads. */
    tipo: z.enum(['PROPUESTA', 'PREGUNTA', 'NADA']),
    mensaje: z.string(),
    /** Respuestas rapidas a la pregunta, si la hay. */
    opciones: z.array(z.string()),
    acciones: z.array(accionPropuestaSchema),
    nuevas: z.array(nuevaPropuestaSchema),
    /** Lo que el modelo propuso y el servidor no admitio, dicho en claro. */
    descartadas: z.array(z.string()),
    /**
     * Lo que el asistente leyo en las capturas, en limpio. Se guarda en la nota
     * como fuente: la imagen no se puede buscar, el texto si.
     */
    transcripcion: z.string().nullable(),
});
const tareaAplicadaSchema = z
    .object({ titulo: z.string().min(2).max(300), vence: fechaAsistenteSchema.nullable() })
    .nullable();
export const aplicarAsistenteSchema = z
    .object({
    /** Lo que escribio la persona, tal cual: queda como fuente de la nota. */
    origen: z.string().max(8000),
    capturas: z.number().int().min(0).max(6),
    transcripcion: z.string().max(8000).nullable(),
    acciones: z
        .array(z.object({
        oportunidadId: z.string().uuid(),
        nota: z.string().max(4000).nullable(),
        nuevaEtapaId: z.string().uuid().nullable(),
        tarea: tareaAplicadaSchema,
    }))
        .max(20)
        .default([]),
    nuevas: z
        .array(z.object({
        nombre: z.string().min(2).max(300),
        pipelineId: z.string().uuid(),
        stageId: z.string().uuid(),
        contactoNombre: z.string().max(200).nullable(),
        contactoEmail: z.string().max(200).nullable(),
        contactoTelefono: z.string().max(60).nullable(),
        contactoEmpresa: z.string().max(200).nullable(),
        origen: z.string().max(300).nullable(),
        valorCents: z.number().int().min(0).nullable(),
        detalles: detallesOportunidadSchema,
        nota: z.string().max(4000).nullable(),
        tarea: tareaAplicadaSchema,
    }))
        .max(10)
        .default([]),
})
    .refine((d) => d.acciones.length + d.nuevas.length > 0, {
    message: 'No hay nada que aplicar',
});
/*
  IMPORTACION DE OPORTUNIDADES DESDE OTRO CRM

  El payload llega YA TRADUCIDO al vocabulario de este producto: nombres de
  embudo y de etapa tal como se llaman aqui. La traduccion desde el sistema de
  origen se hace fuera, con los datos reales delante, igual que la instantanea
  de arranque. Lo que el servidor si hace es RESOLVER esos nombres contra los
  embudos de la empresa, y negarse en redondo si alguno no existe: meter una
  oportunidad en una etapa por defecto porque no se encontro la suya es la clase
  de error que no se ve hasta que alguien busca un lead y no esta donde deberia.
*/
export const oportunidadImportadaSchema = z.object({
    /** Identificador en el sistema de origen. Es la clave de la idempotencia. */
    externalId: z.string().min(1).max(120),
    pipeline: z.string().min(1).max(200),
    stage: z.string().min(1).max(200),
    estado: crmEstadoOportunidadSchema,
    nombre: z.string().min(1).max(300),
    valorCents: z.number().int().min(0),
    moneda: z.string().length(3).optional(),
    origen: z.string().max(300).nullable().optional(),
    /** Nombre de la persona asignada en el origen; se busca entre los usuarios. */
    responsable: z.string().max(200).nullable().optional(),
    contactoNombre: z.string().max(200).nullable().optional(),
    contactoEmail: z.string().max(200).nullable().optional(),
    contactoTelefono: z.string().max(60).nullable().optional(),
    contactoEmpresa: z.string().max(200).nullable().optional(),
    etiquetas: z.array(z.string().max(80)).max(60).optional(),
    motivoPerdida: z.string().max(500).nullable().optional(),
    /** Fecha de alta real en el origen, no la del import. */
    creadoEn: z.string().min(10).max(40),
    actualizadoEn: z.string().min(10).max(40).nullable().optional(),
    datosExternos: z.record(z.string()).optional(),
    /*
      SU HISTORIA. Sin ella, una auditoria como la del 04/11 llega al CRM como
      sesenta nombres en columnas: lo que se sabe de cada uno —quien dijo que y
      cuando, que quedo pendiente— se quedaba en el documento.
  
      Cada nota y cada tarea trae su propio identificador de origen: subir el
      fichero dos veces no las duplica.
    */
    notas: z
        .array(z.object({
        externalId: z.string().min(1).max(160),
        fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        /** Quien la escribio en el origen. Se busca entre los usuarios. */
        autor: z.string().max(200).nullable().optional(),
        texto: z.string().min(1).max(4000),
    }))
        .max(200)
        .optional(),
    tareas: z
        .array(z.object({
        externalId: z.string().min(1).max(160),
        titulo: z.string().min(2).max(300),
        vence: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
        hecha: z.boolean(),
        responsable: z.string().max(200).nullable().optional(),
    }))
        .max(100)
        .optional(),
    /** Quien la lleva EN EXCLUSIVA. Se busca entre los usuarios, como el responsable. */
    exclusivo: z.string().max(200).nullable().optional(),
});
export const etapaFaltanteSchema = z.object({
    pipeline: z.string().min(1).max(200),
    nombre: z.string().min(1).max(200),
    probabilidad: z.number().min(0).max(100),
    esGanada: z.boolean().optional(),
    esPerdida: z.boolean().optional(),
    color: z.string().max(20).nullable().optional(),
});
export const embudoFaltanteSchema = z.object({
    nombre: z.string().min(1).max(200),
    arquetipo: crmArquetipoSchema,
    etapas: z.array(etapaFaltanteSchema.omit({ pipeline: true })).min(1).max(40),
});
/**
 * Quien es quien, cuando el nombre no basta.
 *
 * En el sistema de origen la gente figura con nombre y apellido; aqui puede
 * tener cuenta con solo el nombre de pila. «Katie Rejhon» y «Katie» son la misma
 * persona y ningun algoritmo deberia decidir eso por su cuenta: se dice aqui,
 * con el correo, que es lo unico que identifica a alguien sin ambiguedad.
 */
export const aliasUsuarioSchema = z.object({
    origen: z.string().min(1).max(200),
    email: z.string().email().max(200),
});
export const importarOportunidadesSchema = z.object({
    fuente: z.string().min(1).max(40),
    usuarios: z.array(aliasUsuarioSchema).max(100).optional(),
    /** Embudos que tienen que existir antes de importar. Se crean si faltan. */
    embudos: z.array(embudoFaltanteSchema).max(20).optional(),
    /** Etapas sueltas que faltan en embudos que ya existen. */
    etapas: z.array(etapaFaltanteSchema).max(200).optional(),
    oportunidades: z.array(oportunidadImportadaSchema).min(1).max(5000),
    /*
      RECONOCER LO QUE YA ESTABA POR SU NOMBRE.
  
      Un fichero sacado de un documento —no de una exportacion— no trae el
      identificador del CRM anterior, y la importacion de septiembre si lo traia.
      Sin esto, subir la auditoria encima de aquella duplicaria las sesenta y dos
      promotoras. Con esto, si en el mismo embudo hay UNA oportunidad con el mismo
      nombre, se actualiza esa; si hay dos, no se elige: se crea aparte y se avisa.
    */
    fusionarPorNombre: z.boolean().optional(),
    /** Si viene, el import se rechaza cuando el recuento no cuadra. */
    esperado: z
        .object({ total: z.number().int().min(0), sumaCents: z.number().int().min(0) })
        .optional(),
});
export const resumenImportacionCrmSchema = z.object({
    embudosCreados: z.array(z.string()),
    etapasCreadas: z.array(z.string()),
    creadas: z.number().int(),
    actualizadas: z.number().int(),
    porEmbudo: z.array(z.object({ pipeline: z.string(), total: z.number().int(), sumaCents: z.number().int() })),
    total: z.number().int(),
    sumaCents: z.number().int(),
    responsablesResueltos: z.array(z.object({ origen: z.string(), usuario: z.string() })),
    avisos: z.array(z.string()),
    /** Notas y tareas que entraron en ESTA pasada; las repetidas no cuentan. */
    notas: z.number().int(),
    tareas: z.number().int(),
    /** Las que ya estaban y se reconocieron por su nombre. */
    fusionadasPorNombre: z.array(z.string()),
});
// --- Finanzas: tarifas, horas, gastos y facturación ------------------------
/*
  PRECIO_CLIENTE no es un coste: es lo que se le cobra al cliente por cada hora
  de esa persona. Vive en la misma tabla que las tarifas de coste porque se
  fija igual —por persona, por casa, con su vigencia— y se propone igual al
  cargar horas.
*/
export const modalidadTarifaTrabajoSchema = z.enum(['FIJA_MENSUAL', 'POR_HORA', 'PRECIO_CLIENTE']);
export const tipoHoraSchema = z.enum([
    'GESTION', 'OTRO', 'MANTENIMIENTO', 'LIMPIEZA', 'OBRA', 'ADMINISTRACION',
    'COMERCIAL', 'DESPLAZAMIENTO',
]);
export const categoriaGastoSchema = z.enum([
    // Categorias historicas del registro minimo.
    'LOGISTICA', 'COMPRA', 'OTRO',
    // Plan de gastos real de la gestora.
    'MARKETING', 'VEHICULOS', 'PROPERTY_MANAGEMENT', 'OFICINA', 'PROVEEDORES_CLIENTES',
    'EQUIPO', 'GASTOS_GENERALES', 'REAL_ESTATE', 'SOFTWARE', 'SERVICIOS',
    'RECUPERABLES', 'LIMPIEZA', 'GESTORIA', 'OTROS_DIRECTOS',
    // La linea de Gastos que sale de una carga de horas.
    'MANO_DE_OBRA',
]);
export const modoFacturacionSchema = z.enum([
    'FACTURA_CON_IVA',
    'PRESUPUESTO_SIN_IVA',
    'HOLDED',
]);
const fechaISO = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const financeRateSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    userNombre: z.string().nullable(),
    propertyId: z.string().uuid().nullable(),
    propertyNombre: z.string().nullable(),
    modalidad: modalidadTarifaTrabajoSchema,
    importeCents: z.number().int(),
    moneda: z.string(),
    vigenteDesde: z.string(),
    vigenteHasta: z.string().nullable(),
});
export const fijarTarifaTrabajoSchema = z.object({
    userId: z.string().uuid(),
    propertyId: z.string().uuid().nullable().optional(),
    modalidad: modalidadTarifaTrabajoSchema,
    importeCents: z.number().int().min(0),
    moneda: z.string().length(3).optional(),
    vigenteDesde: fechaISO.optional(),
});
/** Lo mas que cabe en una carga de horas: un mes entero, 31 x 24 h. */
export const MAX_MINUTOS_CARGA = 31 * 24 * 60;
export const timeEntrySchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    userNombre: z.string().nullable(),
    propertyId: z.string().uuid().nullable(),
    propertyNombre: z.string().nullable(),
    fecha: z.string(),
    minutos: z.number().int(),
    horas: z.number(),
    tipo: tipoHoraSchema,
    descripcion: z.string().nullable(),
    imputadoPor: z.string().uuid().nullable(),
    /** Lo que Soluxe paga por cada hora de esta carga. Null en cargas antiguas. */
    costeHoraCents: z.number().int().nullable(),
    /** Lo que se cobra al cliente por cada hora. Null en cargas antiguas. */
    precioHoraClienteCents: z.number().int().nullable(),
    /** Si se cobra aparte o va incluida en el fee de management. */
    facturableExtra: z.boolean(),
    incidencias: z.array(z.string().uuid()),
});
export const registrarHorasSchema = z.object({
    // Si se omite, las horas se imputan a quien las registra. La Dirección puede
    // imputarlas a otra persona indicando su identificador.
    userId: z.string().uuid().optional(),
    propertyId: z.string().uuid().nullable().optional(),
    fecha: fechaISO,
    /*
      Hasta un mes entero (31 x 24 h) en una sola carga: un trabajo de varios dias
      —45 horas de una obra— se apunta de una vez, no partido en jornadas.
    */
    minutos: z.number().int().min(1).max(MAX_MINUTOS_CARGA),
    tipo: tipoHoraSchema.optional(),
    descripcion: z.string().max(2000).optional(),
    /*
      LO QUE CUESTA Y LO QUE SE COBRA ESTA CARGA. Si no vienen, se toman de las
      tarifas de la persona. Solo Direccion puede fijarlos a mano: un manager que
      carga sus horas no decide cuanto se le paga por ellas.
    */
    costeHoraCents: z.number().int().min(0).optional(),
    precioHoraClienteCents: z.number().int().min(0).optional(),
    /** Si se cobra aparte al cliente o va dentro del fee. Por defecto, dentro. */
    facturableExtra: z.boolean().optional(),
    /** Las incidencias abiertas a las que responde el trabajo. */
    incidencias: z.array(z.string().uuid()).max(30).optional(),
    /*
      ALGUIEN QUE NO USA LA APLICACION —un proveedor—. Sus horas no son nomina de
      nadie: se guardan como un gasto a su nombre, con su coste y su precio.
      Si viene, se ignora `userId`.
    */
    personaManual: z.string().min(2).max(200).optional(),
});
/** Lo que se propone al cargar horas de una persona en una casa. */
export const tarifaSugeridaSchema = z.object({
    costeHoraCents: z.number().int().nullable(),
    precioHoraClienteCents: z.number().int().nullable(),
});
export const expenseSchema = z.object({
    id: z.string().uuid(),
    propertyId: z.string().uuid().nullable(),
    propertyNombre: z.string().nullable(),
    fecha: z.string(),
    concepto: z.string(),
    categoria: categoriaGastoSchema,
    costeCents: z.number().int(),
    moneda: z.string(),
    margenPb: z.number().int(),
});
// --- Gastos y horas internos (nivel organizacion, sin propiedad) -----------
export const estadoGastoSchema = z.enum([
    'PENDIENTE_APROBACION', 'APROBADO', 'RECHAZADO', 'PAGADO',
]);
export const contabilizacionSchema = z.enum([
    'SIN_CONTABILIZAR', 'CONTABILIZADO', 'EN_ERP', 'PROCESANDO',
]);
/**
 * Un adjunto visto desde una lista: lo justo para abrirlo.
 *
 * Cada gasto lleva su factura. La tabla no necesita el tamaño ni la fecha de
 * subida: necesita saber que existe, cómo se llama y si es una foto o un PDF.
 */
export const adjuntoBreveSchema = z.object({
    id: z.string().uuid(),
    nombreFichero: z.string(),
    tipoMime: z.string(),
});
export const gastoInternoSchema = z.object({
    id: z.string().uuid(),
    fecha: z.string(),
    concepto: z.string(),
    categoria: categoriaGastoSchema,
    costeCents: z.number().int(),
    moneda: z.string(),
    beneficiario: z.string().nullable(),
    estado: estadoGastoSchema,
    contabilizacion: contabilizacionSchema,
    recurrente: z.boolean(),
    anticipo: z.boolean(),
    autorNombre: z.string().nullable(),
    /* Se filtra por persona, y dos personas pueden llamarse igual. */
    autorId: z.string().uuid().nullable(),
    /*
      De que casa es, si es de alguna. «Interno» dejo de querer decir «sin
      propiedad»: la misma pantalla sirve ahora para mirar lo cargado a las casas,
      que antes no se podia ver fila a fila desde ninguna parte.
    */
    propiedadId: z.string().uuid().nullable(),
    propiedadNombre: z.string().nullable(),
    /** Las facturas del gasto. Casi siempre una; a veces ticket y factura. */
    adjuntos: z.array(adjuntoBreveSchema),
});
export const horaInternaSchema = z.object({
    id: z.string().uuid(),
    fecha: z.string(),
    minutos: z.number().int(),
    tipo: tipoHoraSchema,
    descripcion: z.string().nullable(),
    usuarioNombre: z.string(),
    usuarioId: z.string().uuid(),
    propiedadId: z.string().uuid().nullable(),
    propiedadNombre: z.string().nullable(),
    /** Si esas horas se cobran aparte al cliente o van dentro del fee. */
    facturableExtra: z.boolean(),
});
export const resumenInternoSchema = z.object({
    totalCents: z.number().int(),
    moneda: z.string(),
    pendientes: z.number().int(),
    sinContabilizar: z.number().int(),
    totalGastos: z.number().int(),
    totalMinutos: z.number().int(),
    totalRegistrosHoras: z.number().int(),
});
export const registrarGastoSchema = z.object({
    propertyId: z.string().uuid().nullable().optional(),
    fecha: fechaISO,
    concepto: z.string().min(2).max(300),
    categoria: categoriaGastoSchema.optional(),
    costeCents: z.number().int().min(0),
    moneda: z.string().length(3).optional(),
    margenPb: z.number().int().min(0).max(1_000_000).optional(),
    /**
     * Repercutible al cliente. Es lo que decide si el gasto entra en una factura:
     * sin esto, "facturar pendiente" no encontraria nunca nada que facturar.
     */
    facturableCliente: z.boolean().optional(),
    /** Visible en el portal del cliente aunque no se le repercuta. */
    visibleCliente: z.boolean().optional(),
    /** Proveedor del directorio que emitio el gasto. */
    providerId: z.string().uuid().nullable().optional(),
    /** A quien se pago, cuando no esta en el directorio de proveedores. */
    beneficiario: z.string().max(200).nullable().optional(),
    /** Que se hizo: lo que el cliente y el equipo leen al lado del importe. */
    descripcion: z.string().max(2000).nullable().optional(),
    incidencias: z.array(z.string().uuid()).max(30).optional(),
});
export const propertyBillingSchema = z.object({
    propertyId: z.string().uuid(),
    modo: modoFacturacionSchema,
    margenPb: z.number().int(),
    ivaPb: z.number().int(),
    datosFacturacion: z.string().nullable(),
});
export const configurarFacturacionSchema = z.object({
    modo: modoFacturacionSchema.optional(),
    margenPb: z.number().int().min(0).max(1_000_000).optional(),
    ivaPb: z.number().int().min(0).max(1_000_000).optional(),
    datosFacturacion: z.string().max(2000).nullable().optional(),
});
// Resumen mensual: el cálculo del importe a facturar por persona y propiedad.
export const lineaResumenSchema = z.object({
    tipo: z.enum(['HORAS_FIJAS', 'HORAS', 'GASTO']),
    userId: z.string().uuid().nullable(),
    userNombre: z.string().nullable(),
    propertyId: z.string().uuid().nullable(),
    propertyNombre: z.string().nullable(),
    concepto: z.string(),
    horas: z.number().nullable(),
    costeCents: z.number().int(),
    margenPb: z.number().int(),
    baseCents: z.number().int(),
    ivaPb: z.number().int(),
    ivaCents: z.number().int(),
    totalCents: z.number().int(),
    modo: modoFacturacionSchema,
    moneda: z.string(),
});
export const resumenMensualSchema = z.object({
    anio: z.number().int(),
    mes: z.number().int(),
    lineas: z.array(lineaResumenSchema),
    totales: z.object({
        costeCents: z.number().int(),
        baseCents: z.number().int(),
        ivaCents: z.number().int(),
        totalCents: z.number().int(),
        moneda: z.string(),
    }),
});
// --- Proyectos y tareas (estilo Asana) -------------------------------------
export const estadoProyectoSchema = z.enum(['ACTIVO', 'EN_PAUSA', 'COMPLETADO', 'ARCHIVADO']);
export const estadoTareaSchema = z.enum(['TODO', 'EN_CURSO', 'BLOQUEADA', 'HECHA']);
export const prioridadTareaSchema = z.enum(['ALTA', 'MEDIA', 'BAJA']);
/**
 * Quien lleva una tarea.
 *
 * RESPONSABLE: la tarea sale en su dia y cuenta como suya. COLABORADOR: se
 * entera de lo que pasa, pero la tarea no es suya. Son varios a proposito: una
 * tarea de obra la llevan el manager y el de mantenimiento, y elegir a uno de
 * los dos seria falsear quien la esta haciendo.
 */
export const rolEnTareaSchema = z.enum(['RESPONSABLE', 'COLABORADOR']);
export const personaTareaSchema = z.object({
    userId: z.string().uuid(),
    nombre: z.string(),
    rol: rolEnTareaSchema,
});
export const etiquetaTareaSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    color: z.string().nullable(),
});
export const comentarioTareaSchema = z.object({
    id: z.string().uuid(),
    autorNombre: z.string().nullable(),
    texto: z.string(),
    creadoEn: z.string(),
});
export const tareaSchema = z.object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    sectionId: z.string().uuid().nullable(),
    /** Si cuelga de otra tarea. Solo se admite un nivel: una subtarea no tiene hijas. */
    parentTaskId: z.string().uuid().nullable(),
    titulo: z.string(),
    descripcion: z.string().nullable(),
    estado: estadoTareaSchema,
    prioridad: prioridadTareaSchema,
    responsables: z.array(personaTareaSchema),
    etiquetas: z.array(etiquetaTareaSchema),
    propertyId: z.string().uuid().nullable(),
    vence: z.string().nullable(),
    orden: z.number().int(),
    completadaEn: z.string().nullable(),
    creadoEn: z.string(),
    /** Contadores para las senales de la lista, sin traerse el contenido. */
    comentarios: z.number().int(),
    adjuntos: z.number().int(),
    subtareas: z.number().int(),
    subtareasHechas: z.number().int(),
});
/** Una tarea abierta en el panel lateral: con su conversacion y sus hijas. */
export const tareaDetalleSchema = tareaSchema.extend({
    proyectoNombre: z.string(),
    hijas: z.array(tareaSchema),
    conversacion: z.array(comentarioTareaSchema),
});
export const seccionSchema = z.object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    nombre: z.string(),
    orden: z.number().int(),
});
export const proyectoSchema = z.object({
    id: z.string().uuid(),
    teamId: z.string().uuid(),
    nombre: z.string(),
    descripcion: z.string().nullable(),
    estado: estadoProyectoSchema,
    propertyId: z.string().uuid().nullable(),
    orden: z.number().int(),
});
export const equipoSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    color: z.string().nullable(),
    orden: z.number().int(),
    proyectos: z.array(proyectoSchema),
});
/** Un proyecto abierto: sus secciones y todas sus tareas, para pintar cualquier vista. */
export const proyectoDetalleSchema = proyectoSchema.extend({
    secciones: z.array(seccionSchema),
    tareas: z.array(tareaSchema),
});
export const crearEquipoSchema = z.object({
    nombre: z.string().min(2).max(120),
    color: z.string().max(20).optional(),
});
export const crearProyectoSchema = z.object({
    teamId: z.string().uuid(),
    nombre: z.string().min(2).max(200),
    descripcion: z.string().max(2000).optional(),
    propertyId: z.string().uuid().nullable().optional(),
});
export const crearSeccionSchema = z.object({
    nombre: z.string().min(1).max(120),
});
export const crearTareaSchema = z.object({
    titulo: z.string().min(1).max(300),
    descripcion: z.string().max(4000).optional(),
    sectionId: z.string().uuid().nullable().optional(),
    /** Crea una subtarea de esta. El servidor rechaza colgar de una subtarea. */
    parentTaskId: z.string().uuid().nullable().optional(),
    prioridad: prioridadTareaSchema.optional(),
    responsables: z.array(z.string().uuid()).max(20).optional(),
    propertyId: z.string().uuid().nullable().optional(),
    vence: fechaISO.nullable().optional(),
});
export const editarTareaSchema = z.object({
    titulo: z.string().min(1).max(300).optional(),
    descripcion: z.string().max(4000).nullable().optional(),
    estado: estadoTareaSchema.optional(),
    prioridad: prioridadTareaSchema.optional(),
    /** La lista COMPLETA de responsables: lo que no venga, deja de serlo. */
    responsables: z.array(z.string().uuid()).max(20).optional(),
    colaboradores: z.array(z.string().uuid()).max(20).optional(),
    propertyId: z.string().uuid().nullable().optional(),
    vence: fechaISO.nullable().optional(),
});
export const comentarTareaSchema = z.object({
    texto: z.string().min(1).max(4000),
});
export const etiquetarTareaSchema = z.object({
    /** Nombre de la etiqueta. Si no existe en la empresa, se crea. */
    nombre: z.string().min(1).max(60),
});
export const renombrarSchema = z.object({
    nombre: z.string().min(1).max(200),
});
export const moverTareaSchema = z.object({
    sectionId: z.string().uuid().nullable().optional(),
    estado: estadoTareaSchema.optional(),
    orden: z.number().int().min(0).optional(),
});
// --- El dia de una persona --------------------------------------------------
//
// Lo que alguien tiene HOY, venga de donde venga: las tareas de proyecto de las
// que es responsable y las visitas que tiene agendadas. Se responde en una sola
// llamada porque es una sola pregunta: «que me toca hoy».
export const visitaDelDiaSchema = z.object({
    id: z.string().uuid(),
    titulo: z.string(),
    tipo: z.string(),
    hora: z.string().nullable(),
    estado: z.string(),
    propiedadId: z.string().uuid().nullable(),
    propiedadNombre: z.string().nullable(),
});
export const incidenciaDelDiaSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    titulo: z.string(),
    nivel: z.number().int(),
    propiedadId: z.string().uuid(),
    propiedadNombre: z.string().nullable(),
    /** Puede no tener: una incidencia asignada sin fecha tambien es cosa de hoy. */
    fechaEsperada: z.string().nullable(),
    /** Si esta aqui porque es de quien mira, y no solo porque ha vencido. */
    asignadaAMi: z.boolean(),
});
export const miDiaSchema = z.object({
    fecha: z.string(),
    /**
     * Las que siguen abiertas y tienen fecha, ordenadas por ella: primero lo
     * atrasado, luego hoy, luego lo que viene. Como maximo diez.
     */
    tareas: z.array(tareaSchema.extend({ proyectoNombre: z.string() })),
    visitas: z.array(visitaDelDiaSchema),
    /**
     * Incidencias con fecha esperada que ya ha llegado y siguen abiertas. Van
     * aqui por la misma razon que las visitas: quien empieza la jornada tiene una
     * sola pregunta, y mirar en tres sitios acaba en mirar en uno.
     */
    incidencias: z.array(incidenciaDelDiaSchema),
    /*
      Y LAS TAREAS DEL CRM QUE SON SUYAS. Llamar a Christian el jueves es tan
      tarea de hoy como la visita de las once; si solo vive dentro de la ficha de
      la oportunidad, nadie la ve hasta que abre esa ficha, que es justo cuando ya
      no hacía falta acordarse.
    */
    crm: z.array(z.object({
        id: z.string().uuid(),
        titulo: z.string(),
        vence: z.string().nullable(),
        oportunidadId: z.string().uuid(),
        oportunidadNombre: z.string(),
        embudoNombre: z.string(),
    })),
});
// --- Importacion de proyectos ----------------------------------------------
//
// La cartera se importo una vez y la puerta se cerro. Los proyectos vienen
// APARTE y con su propia puerta, porque llegan despues y sobre una empresa que
// ya tiene datos: exigir la cartera vacia aqui haria imposible traerlos.
//
// Cada cosa trae el identificador que tenia en su origen. Con el, volver a
// lanzar la importacion reconoce lo que ya esta en vez de duplicarlo; sin el,
// un segundo intento crearia la cuenta entera por segunda vez.
export const personaExternaSchema = z.object({
    email: z.string().nullable(),
    nombre: z.string(),
});
export const tareaImportadaSchema = z.lazy(() => z.object({
    origenId: z.string().min(1),
    titulo: z.string().min(1).max(300),
    notas: z.string().nullable().optional(),
    completada: z.boolean(),
    vence: z.string().nullable().optional(),
    creadaEn: z.string().nullable().optional(),
    completadaEn: z.string().nullable().optional(),
    seccionOrigenId: z.string().nullable().optional(),
    responsables: z.array(personaExternaSchema).max(20).optional(),
    colaboradores: z.array(personaExternaSchema).max(20).optional(),
    etiquetas: z.array(z.string().max(60)).max(20).optional(),
    subtareas: z.array(tareaImportadaSchema).optional(),
}));
export const proyectoImportadoSchema = z.object({
    origenId: z.string().min(1),
    nombre: z.string().min(1).max(200),
    descripcion: z.string().nullable().optional(),
    archivado: z.boolean().optional(),
    secciones: z.array(z.object({ origenId: z.string().min(1), nombre: z.string().min(1).max(120) })),
    tareas: z.array(tareaImportadaSchema),
});
export const equipoImportadoSchema = z.object({
    origenId: z.string().min(1),
    nombre: z.string().min(1).max(120),
    proyectos: z.array(proyectoImportadoSchema),
});
export const importarProyectosSchema = z.object({
    origen: z.string().min(1).max(40),
    equipos: z.array(equipoImportadoSchema),
});
export const resumenImportacionProyectosSchema = z.object({
    equipos: z.number().int(),
    proyectos: z.number().int(),
    secciones: z.number().int(),
    tareas: z.number().int(),
    subtareas: z.number().int(),
    asignaciones: z.number().int(),
    etiquetas: z.number().int(),
    /** Ya estaban: se reconocieron por su identificador de origen y no se duplicaron. */
    yaEstaban: z.number().int(),
    /**
     * Personas del fichero que no existen en el equipo. Se dicen en voz alta en
     * lugar de dejar la tarea sin responsable en silencio.
     */
    personasSinResolver: z.array(z.string()),
});
// --- Integraciones: bóveda de credenciales por empresa ---------------------
export const proveedorIntegracionSchema = z.enum([
    'HOLDED',
    'STRIPE',
    'GOCARDLESS',
    'OPEN_BANKING',
    'WHATSAPP',
    'EMAIL',
    'FIRMA',
]);
/**
 * Estado de una integración para la pantalla de ajustes. NUNCA incluye el
 * secreto: sólo si está configurada, si está activa, y su config no sensible.
 */
export const estadoIntegracionSchema = z.object({
    proveedor: proveedorIntegracionSchema,
    nombre: z.string(),
    descripcion: z.string(),
    capacidades: z.array(z.string()),
    configurado: z.boolean(),
    activo: z.boolean(),
    etiqueta: z.string().nullable(),
    config: z.record(z.string(), z.unknown()),
    actualizadoEn: z.string().nullable(),
});
export const guardarCredencialSchema = z.object({
    // El secreto (clave de API, token…). Viaja una sola vez al guardarlo y se
    // cifra en reposo; nunca vuelve al cliente.
    secreto: z.string().min(1).max(8000),
    etiqueta: z.string().max(200).optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    activo: z.boolean().optional(),
});
// --- Referidos y comisiones ------------------------------------------------
export const estadoReferidoSchema = z.enum(['PENDIENTE', 'CONVERTIDO', 'DESCARTADO']);
export const tipoComisionSchema = z.enum(['PORCENTAJE', 'FIJO']);
export const referidoSchema = z.object({
    id: z.string().uuid(),
    referidorPartyId: z.string().uuid().nullable(),
    referidorUserId: z.string().uuid().nullable(),
    referidorNombre: z.string().nullable(),
    nombreReferido: z.string(),
    contactoEmail: z.string().nullable(),
    contactoTelefono: z.string().nullable(),
    estado: estadoReferidoSchema,
    opportunityId: z.string().uuid().nullable(),
    propertyId: z.string().uuid().nullable(),
    comisionTipo: tipoComisionSchema,
    comisionValor: z.number().int(),
    baseCents: z.number().int().nullable(),
    comisionCents: z.number().int().nullable(),
    moneda: z.string(),
    motivo: z.string().nullable(),
    convertidoEn: z.string().nullable(),
    creadoEn: z.string(),
});
export const crearReferidoSchema = z
    .object({
    referidorPartyId: z.string().uuid().optional(),
    referidorUserId: z.string().uuid().optional(),
    nombreReferido: z.string().min(2).max(300),
    contactoEmail: z.string().email().max(200).optional(),
    contactoTelefono: z.string().max(60).optional(),
    comisionTipo: tipoComisionSchema.optional(),
    comisionValor: z.number().int().min(0).optional(),
    propertyId: z.string().uuid().optional(),
    opportunityId: z.string().uuid().optional(),
})
    .refine((d) => d.referidorPartyId || d.referidorUserId, {
    message: 'Indica quién refiere: una parte o una persona interna.',
});
export const convertirReferidoSchema = z.object({
    // Base sobre la que se calcula la comisión porcentual. Para comisión fija es
    // opcional (informativa).
    baseCents: z.number().int().min(0).optional(),
    opportunityId: z.string().uuid().optional(),
    propertyId: z.string().uuid().optional(),
});
export const descartarReferidoSchema = z.object({ motivo: motivoSchema });
// --- Proveedores (directorio interno) --------------------------------------
export const oficioSchema = z.enum([
    'FONTANERIA',
    'JARDINERIA',
    'CLIMA',
    'PISCINAS',
    'LIMPIEZA',
    'ELECTRICIDAD',
    'OBRA',
    'SEGURIDAD',
    'OTRO',
]);
export const proveedorSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    oficio: oficioSchema,
    email: z.string().nullable(),
    telefono: z.string().nullable(),
    nif: z.string().nullable(),
    zona: z.string().nullable(),
    notas: z.string().nullable(),
    activo: z.boolean(),
});
export const crearProveedorSchema = z.object({
    nombre: z.string().min(2).max(200),
    oficio: oficioSchema,
    email: z.string().email().max(200).optional(),
    telefono: z.string().max(60).optional(),
    nif: z.string().max(60).optional(),
    zona: z.string().max(120).optional(),
    notas: z.string().max(2000).optional(),
});
export const editarProveedorSchema = z.object({
    nombre: z.string().min(2).max(200).optional(),
    oficio: oficioSchema.optional(),
    email: z.string().email().max(200).nullable().optional(),
    telefono: z.string().max(60).nullable().optional(),
    nif: z.string().max(60).nullable().optional(),
    zona: z.string().max(120).nullable().optional(),
    notas: z.string().max(2000).nullable().optional(),
});
// --- Calendario de eventos -------------------------------------------------
export const tipoEventoSchema = z.enum([
    'VISITA',
    'PREVENTIVA',
    'REVISION',
    'LLEGADA',
    'SALIDA',
    'OTRO',
]);
/*
 * En pantalla: Abierta (PREVISTA), En curso y Cerrada (HECHA). Los nombres
 * guardados no cambian; renombrarlos obligaria a reescribir la agenda entera y
 * no diria nada nuevo.
 */
export const estadoEventoSchema = z.enum(['PREVISTA', 'EN_CURSO', 'HECHA', 'CANCELADA']);
const horaSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const eventoCalendarioSchema = z.object({
    id: z.string().uuid(),
    propiedadId: z.string().uuid().nullable(),
    propiedadCodigo: z.string().nullable(),
    tipo: tipoEventoSchema,
    titulo: z.string(),
    /** La lista que se va a recorrer al llegar. Las citas viejas no la tienen. */
    plantillaId: z.string().uuid().nullable(),
    plantillaNombre: z.string().nullable(),
    fecha: z.string(),
    hora: z.string().nullable(),
    estado: estadoEventoSchema,
    asignadoUserId: z.string().uuid().nullable(),
    asignadoNombre: z.string().nullable(),
    notas: z.string().nullable(),
});
const fechaIsoSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const crearEventoSchema = z.object({
    propiedadId: z.string().uuid().optional(),
    tipo: tipoEventoSchema,
    /*
      EL TITULO SALE DE LA PLANTILLA.
  
      Se escribia a mano y acababa en «Visita semanal», «visita semana» y
      «Semanal» para lo mismo, sin decir que habia que comprobar al llegar.
      Eligiendo la lista, el titulo es su nombre y no hay nada que teclear. Sigue
      aceptandose escrito para las citas que no son una visita con lista.
    */
    plantillaId: z.string().uuid().optional(),
    titulo: z.string().min(2).max(200).optional(),
    fecha: fechaIsoSchema,
    hora: horaSchema.optional(),
    asignadoUserId: z.string().uuid().optional(),
    notas: z.string().max(2000).optional(),
});
export const editarEventoSchema = z.object({
    propiedadId: z.string().uuid().nullable().optional(),
    tipo: tipoEventoSchema.optional(),
    plantillaId: z.string().uuid().nullable().optional(),
    titulo: z.string().min(2).max(200).optional(),
    fecha: fechaIsoSchema.optional(),
    hora: horaSchema.nullable().optional(),
    asignadoUserId: z.string().uuid().nullable().optional(),
    notas: z.string().max(2000).nullable().optional(),
});
export const cambiarEstadoEventoSchema = z.object({ estado: estadoEventoSchema });
// --- Plantillas de checklist -----------------------------------------------
export const tipoChecklistSchema = z.enum([
    'ENTRADA',
    'SALIDA',
    'PERIODICA',
    'MANTENIMIENTO',
    'OTRO',
]);
export const itemChecklistSchema = z.object({
    id: z.string().uuid(),
    seccion: z.string(),
    texto: z.string(),
    orden: z.number().int(),
    critico: z.boolean(),
    exigeFoto: z.boolean(),
});
export const plantillaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    tipo: tipoChecklistSchema,
    activo: z.boolean(),
    totalItems: z.number().int().min(0),
    totalCriticos: z.number().int().min(0),
});
export const plantillaCompletaSchema = plantillaResumenSchema.extend({
    items: z.array(itemChecklistSchema),
});
// --- Importacion de checklists ----------------------------------------------
//
// Las plantillas de visita del gestor anterior. Se reconocen POR NOMBRE, no por
// un identificador de origen: alli no habia ninguno que se pudiera exportar, y
// el nombre es lo unico estable que hay. Repetir la importacion no duplica una
// plantilla que ya este; lo que hace es anadirle los items que le falten.
export const itemChecklistImportadoSchema = z.object({
    seccion: z.string().min(1).max(80),
    texto: z.string().min(2).max(400),
    orden: z.number().int().min(0).optional(),
    critico: z.boolean().optional(),
    exigeFoto: z.boolean().optional(),
});
export const plantillaImportadaSchema = z.object({
    nombre: z.string().min(2).max(200),
    tipo: tipoChecklistSchema.optional(),
    items: z.array(itemChecklistImportadoSchema),
});
export const importarChecklistsSchema = z.object({
    plantillas: z.array(plantillaImportadaSchema).max(100),
});
export const resumenImportacionChecklistsSchema = z.object({
    plantillas: z.number().int(),
    items: z.number().int(),
    /** Ya estaban por nombre: no se han duplicado. */
    plantillasYaEstaban: z.number().int(),
    itemsYaEstaban: z.number().int(),
});
export const crearPlantillaSchema = z.object({
    nombre: z.string().min(2).max(200),
    tipo: tipoChecklistSchema.optional(),
});
export const editarPlantillaSchema = z.object({
    nombre: z.string().min(2).max(200).optional(),
    tipo: tipoChecklistSchema.optional(),
});
export const crearItemChecklistSchema = z.object({
    seccion: z.string().min(1).max(80),
    texto: z.string().min(2).max(400),
    critico: z.boolean().optional(),
    exigeFoto: z.boolean().optional(),
});
export const editarItemChecklistSchema = z.object({
    seccion: z.string().min(1).max(80).optional(),
    texto: z.string().min(2).max(400).optional(),
    orden: z.number().int().min(0).optional(),
    critico: z.boolean().optional(),
    exigeFoto: z.boolean().optional(),
});
// --- Alertas (derivadas del estado real, no persistidas) -------------------
export const tipoAlertaSchema = z.enum(['INCIDENCIA', 'ACTIVACION']);
export const severidadAlertaSchema = z.enum(['ALTA', 'MEDIA', 'BAJA']);
export const alertaSchema = z.object({
    id: z.string(),
    tipo: tipoAlertaSchema,
    severidad: severidadAlertaSchema,
    titulo: z.string(),
    detalle: z.string(),
    propiedadId: z.string().uuid().nullable(),
    propiedadCodigo: z.string().nullable(),
    /** La cosa de la que habla: la incidencia o la propiedad. Es la clave de «visto». */
    entidadId: z.string().uuid(),
    leida: z.boolean(),
});
// --- Piezas de la ficha de propiedad ---------------------------------------
export const estadoEstanciaSchema = z.enum(['PREVISTA', 'EN_CURSO', 'FINALIZADA', 'CANCELADA']);
export const frecuenciaMantenimientoSchema = z.enum([
    'SEMANAL', 'QUINCENAL', 'MENSUAL', 'TRIMESTRAL', 'SEMESTRAL', 'ANUAL', 'PERSONALIZADA',
]);
export const estanciaSchema = z.object({
    id: z.string().uuid(),
    desde: z.string(),
    /** Nula mientras la casa siga ocupada: todavia no se sabe hasta cuando. */
    hasta: z.string().nullable(),
    huesped: z.string().nullable(),
    notas: z.string().nullable(),
    estado: estadoEstanciaSchema,
});
export const crearEstanciaSchema = z.object({
    desde: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Sin fecha de salida, la estancia queda abierta y la casa, ocupada. */
    hasta: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    huesped: z.string().max(200).optional(),
    notas: z.string().max(1000).optional(),
});
/**
 * El estado de ocupacion de una casa, como se pulsa en la ficha.
 *
 * OCUPADA abre una estancia sin fecha de salida; VACIA cierra la que hubiera
 * con la fecha del dia, que es cuando de verdad se sabe.
 */
export const ocupacionSchema = z.object({
    ocupada: z.boolean(),
    huesped: z.string().max(200).nullable().optional(),
});
export const areaMantenimientoSchema = z.enum([
    'INTERIOR', 'EXTERIOR', 'SISTEMAS', 'PISCINA', 'JARDIN', 'OTRO',
]);
export const mantenimientoSchema = z.object({
    id: z.string().uuid(),
    concepto: z.string(),
    area: areaMantenimientoSchema,
    frecuencia: frecuenciaMantenimientoSchema,
    genera: z.enum(['VISITA', 'INCIDENCIA']),
    diasAntelacion: z.number().int(),
    cadenciaDias: z.number().int().nullable(),
    ultimaEn: z.string().nullable(),
    proximaEn: z.string().nullable(),
    providerId: z.string().uuid().nullable(),
    providerNombre: z.string().nullable(),
    activo: z.boolean(),
    notas: z.string().nullable(),
});
export const crearMantenimientoSchema = z.object({
    concepto: z.string().min(2).max(300),
    area: areaMantenimientoSchema.optional(),
    frecuencia: frecuenciaMantenimientoSchema.optional(),
    genera: z.enum(['VISITA', 'INCIDENCIA']).optional(),
    diasAntelacion: z.number().int().min(0).max(365).optional(),
    cadenciaDias: z.number().int().min(1).max(3650).optional(),
    proximaEn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    providerId: z.string().uuid().optional(),
    notas: z.string().max(1000).optional(),
});
export const fotoPropiedadSchema = z.object({
    id: z.string().uuid(),
    url: z.string(),
    titulo: z.string().nullable(),
    portada: z.boolean(),
    orden: z.number().int(),
});
/*
 * Una foto llega de dos maneras: subiendo un fichero —lo normal, la foto que se
 * acaba de hacer con el movil— o pegando la direccion de una galeria externa.
 * Tiene que venir UNA de las dos, y el servidor lo comprueba en vez de fiarse.
 */
export const crearFotoSchema = z
    .object({
    url: z.string().url().max(2000).optional(),
    adjuntoId: z.string().uuid().optional(),
    titulo: z.string().max(200).optional(),
    portada: z.boolean().optional(),
})
    .refine((d) => Boolean(d.url) !== Boolean(d.adjuntoId), {
    message: 'Indica un fichero o una direccion, pero no las dos',
});
export const proveedorVinculadoSchema = z.object({
    id: z.string().uuid(),
    providerId: z.string().uuid(),
    nombre: z.string(),
    oficio: oficioSchema,
    email: z.string().nullable(),
    telefono: z.string().nullable(),
    notas: z.string().nullable(),
});
export const vincularProveedorSchema = z.object({
    providerId: z.string().uuid(),
    notas: z.string().max(1000).optional(),
});
/** Cifras de portada de la ficha, en una sola llamada. */
export const resumenFichaSchema = z.object({
    proximaVisita: z.string().nullable(),
    visitasVencidas: z.number().int(),
    ultimaVisita: z.string().nullable(),
    incidenciasAbiertas: z.number().int(),
    incidenciasPorNivel: z.record(z.string(), z.number().int()),
    gastoTotalCents: z.number().int(),
    moneda: z.string(),
    horasTotales: z.number(),
});
export const tarifaEquipoSchema = z.object({
    userId: z.string().uuid(),
    nombre: z.string(),
    rol: z.string(),
    porHoraCents: z.number().int().nullable(),
    fijaMensualCents: z.number().int().nullable(),
    /** Lo que se cobra al cliente por cada hora de esta persona en esta casa. */
    precioClienteCents: z.number().int().nullable(),
    moneda: z.string(),
});
export const tipoDocumentoSchema = z.enum(['CONTRATO', 'SEGURO', 'GARANTIA', 'FACTURA', 'OTRO']);
export const documentoPropiedadSchema = z.object({
    id: z.string().uuid(),
    titulo: z.string(),
    tipo: tipoDocumentoSchema,
    nombreFichero: z.string().nullable(),
    /** Direccion externa, si el documento vive fuera. */
    url: z.string().nullable(),
    /**
     * El fichero subido (PDF, Word, Excel), si lo hay. Se abre por la ruta de
     * adjuntos, la misma que ya comprueba el permiso sobre la propiedad.
     */
    adjuntoId: z.string().uuid().nullable(),
    tipoMime: z.string().nullable(),
    tamanoBytes: z.number().int().nullable(),
    /** Si el dueno lo ve en su portal. Por defecto si: son los papeles de su casa. */
    visibleCliente: z.boolean(),
    creadoEn: z.string(),
});
export const editarDocumentoSchema = z.object({
    visibleCliente: z.boolean(),
});
/** Un documento tal y como lo ve el dueno: sin rastro de lo que no comparte. */
export const documentoPortalSchema = z.object({
    id: z.string().uuid(),
    titulo: z.string(),
    tipo: tipoDocumentoSchema,
    nombreFichero: z.string().nullable(),
    url: z.string().nullable(),
    adjuntoId: z.string().uuid().nullable(),
    creadoEn: z.string(),
});
export const crearDocumentoSchema = z.object({
    titulo: z.string().min(2).max(300),
    tipo: tipoDocumentoSchema.optional(),
    nombreFichero: z.string().max(300).optional(),
    url: z.string().url().max(2000).optional(),
    visibleCliente: z.boolean().optional(),
});
/** Gasto de una propiedad, con lo que se repercute al cliente. */
/**
 * En que punto esta el DINERO de un gasto adelantado por la gestora.
 *
 * Es distinto de `estado` —que habla de la aprobacion interna— y de
 * `contabilizacion` —que habla de si paso a la contabilidad—. Aqui se responde
 * la pregunta del dia a dia: de lo que hemos puesto por una casa, ¿que falta
 * por facturar, que esta facturado y que ya nos han pagado?
 */
export const estadoCobroSchema = z.enum(['POR_COBRAR', 'FACTURADO', 'COBRADO']);
export const marcarCobroSchema = z.object({ estadoCobro: estadoCobroSchema });
export const gastoPropiedadSchema = z.object({
    id: z.string().uuid(),
    fecha: z.string(),
    concepto: z.string(),
    categoria: categoriaGastoSchema,
    costeCents: z.number().int(),
    moneda: z.string(),
    margenPb: z.number().int(),
    /** Coste mas margen. Se calcula, no se guarda: el margen puede cambiar. */
    precioClienteCents: z.number().int(),
    facturableCliente: z.boolean(),
    visibleCliente: z.boolean(),
    beneficiario: z.string().nullable(),
    estado: estadoGastoSchema,
    contabilizacion: contabilizacionSchema,
    estadoCobro: estadoCobroSchema,
    autorNombre: z.string().nullable(),
    /** Las facturas del gasto. Casi siempre una; a veces ticket y factura. */
    adjuntos: z.array(adjuntoBreveSchema),
    /** Que se hizo, escrito a mano. */
    descripcion: z.string().nullable(),
    /** Las incidencias a las que responde, con su codigo y su titulo. */
    incidencias: z.array(z.object({ id: z.string().uuid(), codigo: z.string(), titulo: z.string() })),
    /*
      SI SALE DE UNA CARGA DE HORAS. Entonces el importe es horas x coste/hora, y
      el precio al cliente horas x precio/hora si es extra, o 0 si va en el fee:
      se corrige desde la carga, no tocando el importe a mano.
    */
    horas: z
        .object({
        timeEntryId: z.string().uuid(),
        minutos: z.number().int(),
        costeHoraCents: z.number().int(),
        precioHoraClienteCents: z.number().int(),
        facturableExtra: z.boolean(),
    })
        .nullable(),
});
export const totalesGastoPropiedadSchema = z.object({
    /** Todo lo gastado en la casa (salvo lo rechazado), se repercuta o no. */
    costeCents: z.number().int(),
    clienteCents: z.number().int(),
    /**
     * Lo que queda por cobrarle al cliente, con el mismo criterio que el Panel:
     * repercutible, aprobado o pagado, «Por cobrar» y sin factura.
     */
    porCobrarCents: z.number().int(),
    porCobrarGastos: z.number().int(),
    /** Lo gastado que NO se le repercute. Es la diferencia que antes no se veia. */
    noRepercutidoCents: z.number().int(),
    noRepercutidoGastos: z.number().int(),
    sinContabilizar: z.number().int(),
    moneda: z.string(),
});
// --- Adjuntos ---------------------------------------------------------------
export const entidadAdjuntoSchema = z.enum([
    'INCIDENCIA', 'PROPIEDAD', 'GASTO', 'DOCUMENTO', 'VISITA',
    // Los logotipos de la marca cuelgan de la empresa, no de ninguna casa.
    'EMPRESA',
    // Las fotos de la obra: la ficha rellenada a mano y como va la construccion.
    'OBRA',
    // Una imagen en una tarea. Una foto dice mas que el comentario que la explica.
    'TAREA',
    /*
      LA FOTO DE UN PUNTO DE LA LISTA DE COMPROBACION.
  
      Cuelga del punto y no de la visita entera: una foto de «Verificar color» es
      del agua de la piscina, y dentro de seis meses se compara con la siguiente
      sin que nadie tenga que acordarse de cual era cual.
    */
    'VISITA_ITEM',
    /** Una puerta del recorrido comercial: la fachada y las mejoras que se ven. */
    'PUERTA',
]);
export const adjuntoSchema = z.object({
    id: z.string().uuid(),
    entidad: entidadAdjuntoSchema,
    entidadId: z.string().uuid(),
    nombreFichero: z.string(),
    tipoMime: z.string(),
    tamanoBytes: z.number().int(),
    origen: z.enum(['EQUIPO', 'PROVEEDOR', 'CLIENTE']),
    visibleCliente: z.boolean(),
    /*
      QUIEN LO SUBIO. Una foto de obra sin nombre al lado es una foto que nadie
      puede comentar: la que importa es «Katie, el 4 de septiembre», porque la
      siguiente se compara con esa para saber como va la obra.
    */
    subidoPorNombre: z.string().nullable(),
    creadoEn: z.string(),
});
// --- Conversacion y presupuestos de la incidencia ---------------------------
export const canalComentarioSchema = z.enum(['INTERNO', 'CLIENTE']);
export const comentarioIncidenciaSchema = z.object({
    id: z.string().uuid(),
    canal: canalComentarioSchema,
    cuerpo: z.string(),
    autorNombre: z.string().nullable(),
    creadoEn: z.string(),
});
export const comentarSchema = z.object({
    canal: canalComentarioSchema,
    cuerpo: z.string().min(1).max(4000),
});
export const presupuestoIncidenciaSchema = z.object({
    id: z.string().uuid(),
    providerId: z.string().uuid().nullable(),
    proveedorNombre: z.string().nullable(),
    importeCents: z.number().int(),
    moneda: z.string(),
    estado: z.enum(['SOLICITADO', 'RECIBIDO', 'ACEPTADO', 'RECHAZADO']),
    notas: z.string().nullable(),
    creadoEn: z.string(),
});
export const registrarPresupuestoSchema = z.object({
    providerId: z.string().uuid().optional(),
    proveedorNombre: z.string().max(200).optional(),
    importeCents: z.number().int().min(0),
    notas: z.string().max(1000).optional(),
});
export const actualizarIncidenciaSchema = z.object({
    providerId: z.string().uuid().nullable().optional(),
    visibleCliente: z.boolean().optional(),
    visiblePromotora: z.boolean().optional(),
    costeEstimadoCents: z.number().int().min(0).nullable().optional(),
    slaHoras: z.number().int().min(1).max(8760).nullable().optional(),
    area: areaIncidenciaSchema.optional(),
    /** `null` la quita: dejar de prometer una fecha es una decision valida. */
    fechaEsperada: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    /** `null` la deja sin responsable. */
    responsableId: z.string().uuid().nullable().optional(),
});
// --- El panel de quien no dirige la empresa ---------------------------------
//
// El panel era uno solo, el de Direccion, y el equipo veia el mismo: margen del
// mes, coste soportado, facturable a clientes, rendimiento de sus companeros.
// Eso es la cuenta de resultados de la empresa en la portada de todo el mundo.
export const visitaProximaSchema = z.object({
    id: z.string().uuid(),
    fecha: z.string(),
    hora: z.string().nullable(),
    titulo: z.string(),
    tipo: z.string(),
    propiedadId: z.string().uuid().nullable(),
    propiedadNombre: z.string().nullable(),
});
export const panelPropioSchema = z.object({
    anio: z.number().int(),
    mes: z.number().int(),
    /** Lo suyo, de lo que va de mes. */
    horasMinutos: z.number().int(),
    partesDeTrabajo: z.number().int(),
    gastosCents: z.number().int(),
    gastosRegistros: z.number().int(),
    /**
     * Lo que le corresponde cobrar por esas horas. Sale del mismo motor con el
     * que se factura, no de una multiplicacion aparte: dos cifras distintas para
     * la misma pregunta acabarian en que cada uno cree la que le conviene.
     */
    aCobrarCents: z.number().int(),
    /** De SUS casas. */
    incidencias: z.object({
        n1: z.number().int(),
        n2: z.number().int(),
        n3: z.number().int(),
        n4: z.number().int(),
        total: z.number().int(),
    }),
    ocupacion: z.object({ ocupadas: z.number().int(), total: z.number().int() }),
    proximasVisitas: z.array(visitaProximaSchema),
});
// --- Resumen mensual del portal del cliente ---------------------------------
//
// Un resumen es un DOCUMENTO, no una respuesta improvisada: nace en borrador,
// se revisa, se corrige a mano si hace falta y se publica. Solo entonces lo ve
// el propietario.
export const estadoResumenMesSchema = z.enum(['BORRADOR', 'PUBLICADO']);
export const resumenMesSchema = z.object({
    id: z.string().uuid(),
    propiedadId: z.string().uuid(),
    propiedadNombre: z.string(),
    anio: z.number().int(),
    mes: z.number().int(),
    idioma: z.enum(['es', 'en', 'de']),
    texto: z.string(),
    estado: estadoResumenMesSchema,
    /** Que modelo lo escribio. `null` en los corregidos a mano por una persona. */
    modelo: z.string().nullable(),
    generadoEn: z.string(),
    publicadoEn: z.string().nullable(),
});
export const generarResumenMesSchema = z.object({
    propiedadId: z.string().uuid(),
    anio: z.number().int().min(2000).max(2100),
    mes: z.number().int().min(1).max(12),
    idioma: z.enum(['es', 'en', 'de']),
});
export const editarResumenMesSchema = z.object({
    texto: z.string().min(10).max(4000),
});
// --- Avisos -----------------------------------------------------------------
/*
  LOS HECHOS POR LOS QUE SUENA EL TELEFONO.

  Los seis primeros son avisos que la aplicacion deduce del estado —una
  incidencia critica abierta, una visita que ya paso— y se ven en la campana.
  Los seis ultimos son HECHOS: ocurren una vez, en un instante, y por eso se
  mandan al movil. Cada persona los enciende y los apaga uno a uno.
*/
export const tipoAvisoSchema = z.enum([
    'INCIDENCIA_CRITICA', 'VISITA_VENCIDA', 'GASTO_PENDIENTE',
    'PAGO_RECURRENTE', 'FACTURA_VENCIDA', 'MANTENIMIENTO_VENCIDO', 'OTRO',
    /** El cliente reporta una incidencia desde su portal. */
    'CLIENTE_INCIDENCIA',
    /** El cliente avisa de que viene: fechas de su estancia. */
    'CLIENTE_ESTANCIA',
    /** El cliente escribe en la conversacion de una incidencia. */
    'CLIENTE_MENSAJE',
    /** Alguien del equipo cierra una incidencia. */
    'INCIDENCIA_RESUELTA',
    /** Un gasto por encima del umbral (100 EUR de fabrica). */
    'GASTO_ALTO',
    /** Te ponen a ti como responsable de una incidencia. */
    'ASIGNADA_A_TI',
    /** Se abre una incidencia en una casa que llevas. */
    'INCIDENCIA_NUEVA',
    /** El equipo contesta en la conversacion: le suena al dueno. */
    'RESPUESTA_EQUIPO',
    /** Se apunta una estancia en la casa. */
    'ESTANCIA_NUEVA',
]);
export const avisoSchema = z.object({
    id: z.string(),
    tipo: tipoAvisoSchema,
    titulo: z.string(),
    detalle: z.string().nullable(),
    entidad: z.string().nullable(),
    entidadId: z.string().uuid(),
    leido: z.boolean(),
});
export const cumplimientoPersonaSchema = z.object({
    nombre: z.string(),
    agendadas: z.number().int(),
    completadas: z.number().int(),
    /** -1 cuando no hay visitas agendadas: no hay cumplimiento que medir. */
    porcentaje: z.number().int(),
    horas: z.number(),
});
// --- Cuadro de mando completo ------------------------------------------------
export const filaFacturarSchema = z.object({
    propiedadId: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    gastos: z.number().int(),
    esperaDias: z.number().int(),
    importeCents: z.number().int(),
});
export const incidenciaCriticaSchema = z.object({
    id: z.string().uuid(),
    propiedadId: z.string().uuid(),
    titulo: z.string(),
    nivel: z.number().int(),
    codigo: z.string(),
    propiedadNombre: z.string(),
    costeEstimadoCents: z.number().int().nullable(),
});
export const filaResumenPropiedadSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    nivelNio: z.number().int().nullable(),
    semaforo: semaforoSchema,
    ultimaVisita: z.string().nullable(),
    proximaVisita: z.string().nullable(),
    incidencias: z.object({
        n1: z.number().int(),
        n2: z.number().int(),
        n3: z.number().int(),
        n4: z.number().int(),
    }),
});
export const estanciaPanelSchema = z.object({
    propiedadId: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    desde: z.string(),
    hasta: z.string(),
    noches: z.number().int(),
});
export const visitaPanelSchema = z.object({
    id: z.string().uuid(),
    propiedadId: z.string().uuid(),
    titulo: z.string(),
    fecha: z.string(),
    hora: z.string().nullable(),
    codigo: z.string(),
    propiedadNombre: z.string(),
});
export const horasTrabajadorSchema = z.object({
    nombre: z.string(),
    horas: z.number(),
    propiedades: z.array(z.object({ codigo: z.string(), horas: z.number() })),
});
export const eventoPanelSchema = z.object({
    id: z.string().uuid(),
    tipo: z.string(),
    titulo: z.string(),
    detalle: z.string().nullable(),
    codigo: z.string(),
    ocurridoEn: z.string(),
});
export const panelCompletoSchema = z.object({
    anio: z.number().int(),
    mes: z.number().int(),
    moneda: z.string(),
    pendienteFacturar: z.array(filaFacturarSchema),
    pendienteFacturarTotalCents: z.number().int(),
    incidenciasPorNivel: z.record(z.string(), z.number().int()),
    incidenciasTotal: z.number().int(),
    incidenciasCriticas: z.array(incidenciaCriticaSchema),
    resumenPropiedades: z.array(filaResumenPropiedadSchema),
    ocupadasAhora: z.array(estanciaPanelSchema),
    proximasLlegadas: z.array(estanciaPanelSchema),
    proximasVisitas: z.array(visitaPanelSchema),
    horasPorTrabajador: z.array(horasTrabajadorSchema),
    kpis: z.object({
        costeClientesCents: z.number().int(),
        facturableClientesCents: z.number().int(),
        margenCents: z.number().int(),
        gastoInternoCents: z.number().int(),
    }),
    actividadReciente: z.array(eventoPanelSchema),
    facturasPorCobrar: z.array(z.object({
        id: z.string().uuid(),
        numero: z.string(),
        propiedadNombre: z.string(),
        totalCents: z.number().int(),
        fechaEmision: z.string().nullable(),
        diasVencida: z.number().int().nullable(),
    })),
    facturasPorCobrarTotalCents: z.number().int(),
    cumplimiento: z.array(cumplimientoPersonaSchema),
});
// --- Encargos a proveedores ---------------------------------------------------
/*
 * Lo que ve un proveedor de una incidencia es DELIBERADAMENTE POBRE.
 *
 * No hay aqui notas internas, ni propietario, ni coste real, ni margen, ni los
 * demas proveedores a los que se haya pedido presupuesto. Si un campo no esta
 * declarado en este bloque, el portal del proveedor no puede ensenarlo aunque
 * el servidor lo tuviera a mano.
 *
 * La direccion de la propiedad SI aparece: sin ella no puede ir a trabajar.
 */
export const ESTADOS_ENCARGO = [
    'ASIGNADO',
    'ACEPTADO',
    'EN_CURSO',
    'FIN_SOLICITADA',
    'COMPLETADO',
    'RECHAZADO',
];
export const estadoEncargoSchema = z.enum(ESTADOS_ENCARGO);
export const encargoSchema = z.object({
    id: z.string().uuid(),
    estado: estadoEncargoSchema,
    instrucciones: z.string().nullable(),
    motivoRechazo: z.string().nullable(),
    creadoEn: z.string(),
    /** La incidencia, recortada a lo que un proveedor necesita para trabajar. */
    incidenciaId: z.string().uuid(),
    codigo: z.string(),
    titulo: z.string(),
    descripcion: z.string().nullable(),
    area: z.string().nullable(),
    nivel: z.number().int(),
    propiedadNombre: z.string(),
    propiedadDireccion: z.string().nullable(),
    propiedadZona: z.string().nullable(),
    /** Adjuntos que el equipo ha compartido, mas los que ha subido el proveedor. */
    fotos: z.array(z.string().uuid()),
    /** Su propio presupuesto, si ya lo presento. */
    presupuestoCents: z.number().int().nullable(),
    presupuestoEstado: z.string().nullable(),
});
export const crearEncargoSchema = z.object({
    proveedorId: z.string().uuid(),
    instrucciones: z.string().max(2000).optional(),
});
export const rechazarEncargoSchema = z.object({
    motivo: z.string().min(3).max(500),
});
export const presupuestoProveedorSchema = z.object({
    importeCents: z.number().int().min(0),
    notas: z.string().max(2000).optional(),
});
// --- Equipo: gestion de usuarios ----------------------------------------------
/*
 * ROL y FUNCION son cosas distintas y no se mezclan.
 *
 *   - `rol` es el PERMISO: lo leen las politicas de fila y cada exigirRol.
 *   - `funcion` es el OFICIO en la empresa: dice a que se dedica una persona,
 *     no a que datos llega.
 *
 * Un jardinero que atiende tres casas es, en permisos, un MANAGER de esas tres
 * casas. Lo que le distingue de un gestor es la funcion, no el alcance.
 */
export const FUNCIONES_EQUIPO = [
    'DIRECCION',
    'MANAGER',
    'MARKETING',
    'REAL_ESTATE',
    'MANTENIMIENTO',
    'JARDINERIA',
    'LIMPIEZA',
    'ADMINISTRACION',
    'OTRA',
];
export const funcionEquipoSchema = z.enum(FUNCIONES_EQUIPO);
export const rolAsignacionSchema = z.enum(['PRINCIPAL', 'SECUNDARIO']);
export const propiedadDeUsuarioSchema = z.object({
    propiedadId: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    /** PRINCIPAL/SECUNDARIO para el equipo; OWNER y demas para un propietario. */
    rol: z.string(),
});
export const usuarioEquipoSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    email: z.string(),
    rol: rolSchema,
    funcion: funcionEquipoSchema.nullable(),
    idioma: z.string(),
    activo: z.boolean(),
    /** Si entra en el CRM. Direccion siempre; al resto se lo concede la direccion. */
    crmHabilitado: z.boolean(),
    /** Party vinculada; solo la tienen los clientes. */
    partyId: z.string().uuid().nullable(),
    ultimoAccesoEn: z.string().nullable(),
    propiedades: z.array(propiedadDeUsuarioSchema),
    /**
     * Si ya registro trabajo, no se puede borrar sin falsear el historico. La
     * pantalla lo usa para explicar por que solo se puede ocultar.
     */
    tieneHistorico: z.boolean(),
});
export const crearUsuarioSchema = z.object({
    nombre: z.string().min(2).max(200),
    email: z.string().email().max(320),
    password: z.string().min(8).max(200),
    rol: rolSchema,
    funcion: funcionEquipoSchema.optional(),
    idioma: z.enum(['es', 'en', 'de']).optional(),
});
export const editarUsuarioSchema = z.object({
    nombre: z.string().min(2).max(200).optional(),
    email: z.string().email().max(320).optional(),
    rol: rolSchema.optional(),
    funcion: funcionEquipoSchema.nullable().optional(),
    idioma: z.enum(['es', 'en', 'de']).optional(),
    /** El acceso al CRM se concede y se quita persona a persona. */
    crmHabilitado: z.boolean().optional(),
});
export const restablecerPasswordSchema = z.object({
    password: z.string().min(8).max(200),
});
export const asignarPropiedadSchema = z.object({
    propiedadId: z.string().uuid(),
    rol: rolAsignacionSchema.optional(),
});
export const miembroProyectoSchema = z.object({
    id: z.string().uuid(),
    usuarioId: z.string().uuid(),
    nombre: z.string(),
    funcion: funcionEquipoSchema.nullable(),
    rol: z.enum(['RESPONSABLE', 'MIEMBRO']),
});
export const anadirMiembroSchema = z.object({
    usuarioId: z.string().uuid(),
    rol: z.enum(['RESPONSABLE', 'MIEMBRO']).optional(),
});
// --- Portal del propietario ---------------------------------------------------
/*
 * Lo que el cliente ve en su portal. Es deliberadamente MAS POBRE que la ficha
 * interna: aqui no hay coste real, ni margen, ni quien de nuestro equipo hizo
 * que, ni comentarios del canal interno. Si un campo no esta declarado en este
 * bloque, el portal no puede ensenarlo aunque el servidor lo tuviera a mano.
 */
export const mensajePortalSchema = z.object({
    id: z.string().uuid(),
    cuerpo: z.string(),
    autor: z.string(),
    deEquipo: z.boolean(),
    creadoEn: z.string(),
});
export const incidenciaPortalSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    titulo: z.string(),
    descripcion: z.string().nullable(),
    nivel: z.number().int(),
    abierta: z.boolean(),
    fecha: z.string(),
    /** Identificadores de los adjuntos compartidos; el contenido va por su ruta. */
    fotos: z.array(z.string().uuid()),
    mensajes: z.array(mensajePortalSchema),
});
export const estanciaPortalSchema = z.object({
    id: z.string().uuid(),
    desde: z.string(),
    hasta: z.string().nullable(),
});
export const portalPropiedadSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    zona: z.string().nullable(),
    semaforo: semaforoSchema,
    visitasEsteAnio: z.number().int(),
    proximaVisita: z.string().nullable(),
    /** La ultima que se hizo. El dueno pregunta las dos: cuando vinisteis y cuando volveis. */
    ultimaVisita: z.string().nullable(),
    /** Su casa, con su foto. Nula mientras no haya ninguna subida. */
    fotoPortadaUrl: z.string().nullable(),
    alCorrienteDePago: z.boolean(),
    incidencias: z.array(incidenciaPortalSchema),
    estancias: z.array(estanciaPortalSchema),
    datosFacturacion: z.string().nullable(),
    /** Los papeles de su casa: contratos, facturas, revisiones. */
    documentos: z.array(documentoPortalSchema),
});
export const portalClienteSchema = z.object({
    propiedades: z.array(portalPropiedadSchema),
});
export const reportarIncidenciaSchema = z.object({
    titulo: z.string().min(3).max(300),
    descripcion: z.string().max(2000).optional(),
    /*
     * El dueno dice la urgencia que ve. No es la ultima palabra —el equipo puede
     * cambiarla al revisarla— pero preguntarsela y luego ignorarla seria peor que
     * no preguntar: quien reporta una fuga a las tres de la manana sabe que no es
     * una mejora estetica.
     */
    nivel: nivelIncidenciaSchema.optional(),
});
export const avisarEstanciaSchema = z.object({
    desde: fechaISO,
    hasta: fechaISO.optional(),
});
export const mensajePortalEntradaSchema = z.object({
    cuerpo: z.string().min(1).max(4000),
});
/** El dueno corrige lo que escribio: el titulo, la descripcion, o los dos. */
export const editarIncidenciaPortalSchema = z
    .object({
    titulo: z.string().min(3).max(300).optional(),
    descripcion: z.string().max(2000).nullable().optional(),
})
    .refine((d) => d.titulo !== undefined || d.descripcion !== undefined, {
    message: 'No hay nada que cambiar',
});
export const cambiarPasswordSchema = z.object({
    actual: z.string().min(1).max(200),
    nueva: z.string().min(8).max(200),
});
export const datosFacturacionSchema = z.object({
    datos: z.string().min(2).max(2000),
});
// --- Facturacion --------------------------------------------------------------
export const estadoFacturaSchema = z.enum(['BORRADOR', 'EMITIDA', 'COBRADA', 'ANULADA']);
export const facturaSchema = z.object({
    id: z.string().uuid(),
    propiedadId: z.string().uuid(),
    propiedadCodigo: z.string(),
    propiedadNombre: z.string(),
    numero: z.string(),
    estado: estadoFacturaSchema,
    fechaEmision: z.string().nullable(),
    fechaVencimiento: z.string().nullable(),
    fechaCobro: z.string().nullable(),
    baseCents: z.number().int(),
    ivaPb: z.number().int(),
    ivaCents: z.number().int(),
    totalCents: z.number().int(),
    moneda: z.string(),
    /** Dias desde el vencimiento. Negativo = aun no vence. Null si no aplica. */
    diasVencida: z.number().int().nullable(),
});
export const lineaFacturaSchema = z.object({
    id: z.string().uuid(),
    expenseId: z.string().uuid().nullable(),
    concepto: z.string(),
    importeCents: z.number().int(),
});
export const facturaDetalleSchema = facturaSchema.extend({
    lineas: z.array(lineaFacturaSchema),
});
export const anularFacturaSchema = z.object({ motivo: motivoSchema });
// --- Ejecucion de visita --------------------------------------------------------
export const resultadoItemSchema = z.enum(['PENDIENTE', 'OK', 'INCIDENCIA', 'NO_APLICA']);
export const itemEjecucionSchema = z.object({
    id: z.string().uuid(),
    seccion: z.string(),
    texto: z.string(),
    critico: z.boolean(),
    /** La plantilla pide foto de este punto: sin ella no se puede marcar. */
    exigeFoto: z.boolean(),
    orden: z.number().int(),
    resultado: resultadoItemSchema,
    notas: z.string().nullable(),
    /** Lo que se fotografio aqui. */
    fotos: z.array(adjuntoBreveSchema),
});
export const ejecucionVisitaSchema = z.object({
    id: z.string().uuid(),
    propiedadId: z.string().uuid(),
    eventoId: z.string().uuid().nullable(),
    plantillaNombre: z.string().nullable(),
    estado: z.enum(['EN_CURSO', 'COMPLETADA', 'ABANDONADA']),
    iniciadaEn: z.string(),
    completadaEn: z.string().nullable(),
    ejecutorNombre: z.string().nullable(),
    notas: z.string().nullable(),
    items: z.array(itemEjecucionSchema),
});
export const iniciarVisitaSchema = z.object({
    plantillaId: z.string().uuid().optional(),
    eventoId: z.string().uuid().optional(),
    /*
      EL MOTIVO, CUANDO NO HAY LISTA.
  
      Una visita sin lista no recorre nada: se va por algo concreto —el aviso de
      un vecino, una llave que hay que dejar, el albanil que entra el jueves— y
      eso era justo lo que no se podia escribir en ninguna parte. Se guardaba la
      visita sin una palabra de por que, y al mes siguiente nadie sabia que se
      fue a hacer aquel dia.
    */
    notas: z.string().max(2000).optional(),
});
export const marcarItemSchema = z.object({
    resultado: resultadoItemSchema,
    notas: z.string().max(1000).optional(),
});
// --- Inteligencia artificial y analítica -----------------------------------
export const estadoIaSchema = z.object({
    disponible: z.boolean(),
    proveedor: z.string().nullable(),
});
/**
 * El resultado de PROBAR la conexion, que no es lo mismo que estar configurada.
 * `estadoIaSchema.disponible` dice que hay variables puestas; esto dice que la
 * clave funciona de verdad.
 */
export const pruebaIaSchema = z.object({
    ok: z.boolean(),
    detalle: z.string(),
    modelo: z.string().nullable(),
    tokensEntrada: z.number().int().nullable(),
    tokensSalida: z.number().int().nullable(),
});
export const tipoAnomaliaSchema = z.enum(['OUTLIER', 'DUPLICADO']);
export const severidadSchema = z.enum(['ALTA', 'MEDIA', 'BAJA']);
export const anomaliaGastoSchema = z.object({
    gastoId: z.string().uuid(),
    propertyId: z.string().uuid().nullable(),
    propertyNombre: z.string().nullable(),
    concepto: z.string(),
    costeCents: z.number().int(),
    moneda: z.string(),
    fecha: z.string(),
    tipo: tipoAnomaliaSchema,
    severidad: severidadSchema,
    mensaje: z.string(),
});
export const fuenteClasificacionSchema = z.enum(['IA', 'HEURISTICA']);
export const clasificacionIncidenciaSchema = z.object({
    /** La escala del sistema: N1 urgencia … N4 mejora estetica. El 1 es lo grave. */
    nivelSugerido: nivelIncidenciaSchema,
    categoria: z.string(),
    palabrasClave: z.array(z.string()),
    fuente: fuenteClasificacionSchema,
});
export const clasificarIncidenciaSchema = z.object({
    texto: z.string().min(3).max(1000),
});
// --- Panel / Dashboard -----------------------------------------------------
export const panelResumenSchema = z.object({
    anio: z.number().int(),
    mes: z.number().int(),
    propiedades: z.number().int(),
    propiedadesActivas: z.number().int(),
    clientes: z.number().int(),
    incidenciasAbiertas: z.number().int(),
    incidenciasCriticas: z.number().int(),
    requierenAtencion: z.number().int(),
    horasMes: z.number(),
    costeMesCents: z.number().int(),
    facturableMesCents: z.number().int(),
    margenMesCents: z.number().int(),
    gastosInternosCents: z.number().int(),
    moneda: z.string(),
});
// --- Extracto del propietario ----------------------------------------------
export const lineaExtractoPropiedadSchema = z.object({
    propertyId: z.string().uuid().nullable(),
    propertyNombre: z.string().nullable(),
    baseCents: z.number().int(),
    ivaCents: z.number().int(),
    totalCents: z.number().int(),
    lineas: z.array(lineaResumenSchema),
});
export const extractoPropietarioSchema = z.object({
    partyId: z.string().uuid(),
    partyNombre: z.string().nullable(),
    anio: z.number().int(),
    mes: z.number().int(),
    propiedades: z.array(lineaExtractoPropiedadSchema),
    totales: z.object({
        baseCents: z.number().int(),
        ivaCents: z.number().int(),
        totalCents: z.number().int(),
        moneda: z.string(),
    }),
});
// --- Importación de una instantánea ----------------------------------------
/*
 * INSTANTÁNEA DE ARRANQUE
 *
 * La forma de entrar los datos que una empresa ya tenía antes de usar esto.
 *
 * Es DELIBERADAMENTE genérica: habla el vocabulario de este producto, no el del
 * sistema de origen. Traducir los códigos del origen (sus categorías, sus
 * estados, sus nombres repetidos) es trabajo de quien prepara el fichero, fuera
 * del servidor. Aquí no hay ni una tabla de equivalencias ni el nombre de
 * ninguna empresa: el servidor recibe datos ya en su propio idioma y lo único
 * que hace es validarlos y escribirlos.
 *
 * Las filas se enlazan entre sí por una CLAVE de texto que trae el propio
 * fichero (`clave`), no por identificadores de base de datos. Así el fichero se
 * puede leer y revisar antes de subirlo, que es justo lo que hay que poder
 * hacer con los datos reales de una empresa.
 */
const claveSchema = z.string().min(1).max(120);
const fechaInstantaneaSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const momentoSchema = z.string().min(10).max(40);
export const usuarioInstantaneaSchema = z.object({
    clave: claveSchema,
    nombre: z.string().min(2).max(200),
    email: z.string().email().max(320),
    rol: rolSchema,
    funcion: funcionEquipoSchema.nullable().optional(),
    idioma: idiomaSchema.optional(),
});
export const propiedadInstantaneaSchema = z.object({
    clave: claveSchema,
    codigo: z.string().min(1).max(40),
    nombre: z.string().min(1).max(300),
    direccion: z.string().max(500).nullable().optional(),
    zona: z.string().max(200).nullable().optional(),
    pais: z.string().length(2).optional(),
    tipo: tipoPropiedadSchema,
    nivelNio: nivelNioSchema.nullable().optional(),
    semaforo: semaforoSchema.optional(),
});
export const proveedorInstantaneaSchema = z.object({
    clave: claveSchema,
    nombre: z.string().min(1).max(200),
    oficio: oficioSchema,
    email: z.string().max(320).nullable().optional(),
    telefono: z.string().max(60).nullable().optional(),
    nif: z.string().max(40).nullable().optional(),
    zona: z.string().max(200).nullable().optional(),
    notas: z.string().max(2000).nullable().optional(),
    activo: z.boolean().optional(),
});
export const gastoInstantaneaSchema = z.object({
    propiedad: claveSchema.nullable().optional(),
    fecha: fechaInstantaneaSchema,
    concepto: z.string().min(1).max(300),
    categoria: categoriaGastoSchema,
    costeCents: z.number().int(),
    moneda: z.string().length(3).optional(),
    margenPb: z.number().int().min(0).max(100000).optional(),
    beneficiario: z.string().max(200).nullable().optional(),
    estado: estadoGastoSchema.optional(),
    contabilizado: z.boolean().optional(),
    recurrente: z.boolean().optional(),
    anticipo: z.boolean().optional(),
    facturableCliente: z.boolean().optional(),
    visibleCliente: z.boolean().optional(),
    proveedor: claveSchema.nullable().optional(),
    autor: claveSchema.nullable().optional(),
});
export const horaInstantaneaSchema = z.object({
    usuario: claveSchema,
    propiedad: claveSchema.nullable().optional(),
    fecha: fechaInstantaneaSchema,
    minutos: z.number().int().min(1).max(1440),
    tipo: tipoHoraSchema,
    descripcion: z.string().max(1000).nullable().optional(),
});
export const incidenciaInstantaneaSchema = z.object({
    propiedad: claveSchema,
    codigo: z.string().min(1).max(40),
    titulo: z.string().min(1).max(300),
    descripcion: z.string().max(5000).nullable().optional(),
    nivel: nivelIncidenciaSchema,
    estado: estadoIncidenciaSchema,
    area: areaIncidenciaSchema,
    costeEstimadoCents: z.number().int().nullable().optional(),
    costeFinalCents: z.number().int().nullable().optional(),
    slaHoras: z.number().int().min(0).nullable().optional(),
    visibleCliente: z.boolean().optional(),
    resolucion: z.string().max(5000).nullable().optional(),
    cerradoEn: momentoSchema.nullable().optional(),
    creadoEn: momentoSchema.nullable().optional(),
});
export const eventoInstantaneaSchema = z.object({
    propiedad: claveSchema,
    tipo: tipoEventoSchema,
    titulo: z.string().min(1).max(300),
    fecha: fechaInstantaneaSchema,
    hora: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .nullable()
        .optional(),
    estado: estadoEventoSchema.optional(),
});
export const mantenimientoInstantaneaSchema = z.object({
    propiedad: claveSchema,
    concepto: z.string().min(1).max(300),
    area: areaMantenimientoSchema,
    frecuencia: frecuenciaMantenimientoSchema,
    genera: z.enum(['VISITA', 'INCIDENCIA']).optional(),
    diasAntelacion: z.number().int().min(0).max(365).optional(),
    proximaEn: fechaInstantaneaSchema.nullable().optional(),
    ultimaEn: fechaInstantaneaSchema.nullable().optional(),
    activo: z.boolean().optional(),
});
export const estanciaInstantaneaSchema = z.object({
    propiedad: claveSchema,
    desde: fechaInstantaneaSchema,
    hasta: fechaInstantaneaSchema,
    notas: z.string().max(2000).nullable().optional(),
    estado: estadoEstanciaSchema.optional(),
});
export const documentoInstantaneaSchema = z.object({
    propiedad: claveSchema,
    titulo: z.string().min(1).max(300),
    tipo: tipoDocumentoSchema.optional(),
    nombreFichero: z.string().max(300).nullable().optional(),
    url: z.string().max(2000).nullable().optional(),
    creadoEn: momentoSchema.nullable().optional(),
});
export const eventoHistoricoInstantaneaSchema = z.object({
    propiedad: claveSchema,
    tipo: z.string().min(1).max(60),
    titulo: z.string().min(1).max(300),
    detalle: z.string().max(5000).nullable().optional(),
    ocurridoEn: momentoSchema,
});
export const instantaneaSchema = z.object({
    version: z.literal(1),
    usuarios: z.array(usuarioInstantaneaSchema).max(2000).optional(),
    propiedades: z.array(propiedadInstantaneaSchema).max(5000).optional(),
    proveedores: z.array(proveedorInstantaneaSchema).max(5000).optional(),
    gastos: z.array(gastoInstantaneaSchema).max(100000).optional(),
    horas: z.array(horaInstantaneaSchema).max(100000).optional(),
    incidencias: z.array(incidenciaInstantaneaSchema).max(100000).optional(),
    eventos: z.array(eventoInstantaneaSchema).max(100000).optional(),
    mantenimiento: z.array(mantenimientoInstantaneaSchema).max(100000).optional(),
    estancias: z.array(estanciaInstantaneaSchema).max(100000).optional(),
    documentos: z.array(documentoInstantaneaSchema).max(100000).optional(),
    historial: z.array(eventoHistoricoInstantaneaSchema).max(200000).optional(),
});
export const importarInstantaneaSchema = z.object({
    instantanea: instantaneaSchema,
    /*
     * Contraseña inicial de las personas que se creen. Viaja en la petición y no
     * dentro del fichero a propósito: un fichero con los datos de la empresa se
     * comparte, se copia y se olvida en una carpeta; una contraseña no debe
     * viajar dentro de él.
     */
    passwordInicial: z.string().min(8).max(200),
});
export const resumenImportacionSchema = z.object({
    usuariosCreados: z.number().int(),
    usuariosReutilizados: z.number().int(),
    propiedades: z.number().int(),
    proveedores: z.number().int(),
    gastos: z.number().int(),
    horas: z.number().int(),
    incidencias: z.number().int(),
    eventos: z.number().int(),
    mantenimiento: z.number().int(),
    estancias: z.number().int(),
    documentos: z.number().int(),
    historial: z.number().int(),
});
export const estadoImportacionSchema = z.object({
    /** La importación solo está abierta si la instalación la ha habilitado. */
    habilitada: z.boolean(),
    /** Si ya hay propiedades, importar duplicaría la cartera y se rechaza. */
    vacia: z.boolean(),
    propiedades: z.number().int(),
});
// --- Llaves de acceso (Face ID) --------------------------------------------
/*
 * Lo que la interfaz sabe de una llave. NO incluye la clave pública ni el
 * identificador de la credencial: no le sirven de nada y no tienen por qué
 * salir del servidor. Lo que necesita es reconocerla —«iPhone de Matías»— y
 * saber cuándo se usó por última vez, que es lo que permite decidir si sobra.
 */
export const llaveAccesoSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    /** Sincronizada en un llavero: aparecerá también en los demás dispositivos. */
    respaldada: z.boolean(),
    creadoEn: z.string(),
    ultimoUsoEn: z.string().nullable(),
});
export const altaLlaveSchema = z.object({
    /*
     * La respuesta del navegador va sin tipar a propósito: es una estructura de
     * la especificación de WebAuthn, la valida la biblioteca que verifica la
     * firma, y copiarla aquí solo crearía una segunda definición que se quedaría
     * atrás. Lo que sí se acota es el nombre, que es texto nuestro.
     */
    respuesta: z.unknown(),
    nombre: z.string().min(1).max(80),
});
export const accesoLlaveSchema = z.object({
    respuesta: z.unknown(),
});
export const revocarLlaveSchema = z.object({
    motivo: z.string().min(3).max(300),
});
// --- Ajustes de la empresa --------------------------------------------------
/*
 * Lo que una empresa configura de sí misma: su identidad fiscal, su marca, su
 * plan de gastos y qué avisos quiere recibir cada persona.
 *
 * No entra aquí la retribución del equipo —vive en Finanzas, que es donde se usa
 * y donde tiene sentido junto a las horas— ni el plan de la suscripción ni las
 * integraciones con un ERP.
 */
const colorHexSchema = z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Un color es #rrggbb');
export const organizacionSchema = z.object({
    nombre: z.string(),
    razonSocial: z.string().nullable(),
    nif: z.string().nullable(),
    direccion: z.string().nullable(),
    codigoPostal: z.string().nullable(),
    poblacion: z.string().nullable(),
    provincia: z.string().nullable(),
    pais: z.string().nullable(),
});
export const guardarOrganizacionSchema = z.object({
    nombre: z.string().min(2).max(200),
    razonSocial: z.string().max(200).nullable().optional(),
    /* Se guarda sin espacios ni guiones: es lo que va en la factura. */
    nif: z.string().max(40).nullable().optional(),
    direccion: z.string().max(300).nullable().optional(),
    codigoPostal: z.string().max(20).nullable().optional(),
    poblacion: z.string().max(120).nullable().optional(),
    provincia: z.string().max(120).nullable().optional(),
    pais: z.string().max(120).nullable().optional(),
});
export const marcaSchema = z.object({
    nombre: z.string(),
    colorAcento: colorHexSchema.nullable(),
    colorCabecera: colorHexSchema.nullable(),
    colorSecundario: colorHexSchema.nullable(),
    /** Direcciones para pintar los logotipos; nulas si no se han subido. */
    logoClaroUrl: z.string().nullable(),
    logoOscuroUrl: z.string().nullable(),
});
export const guardarMarcaSchema = z.object({
    colorAcento: colorHexSchema.nullable().optional(),
    colorCabecera: colorHexSchema.nullable().optional(),
    colorSecundario: colorHexSchema.nullable().optional(),
    /** Adjunto ya subido, o `null` para quitar el logotipo. */
    logoClaroId: z.string().uuid().nullable().optional(),
    logoOscuroId: z.string().uuid().nullable().optional(),
});
/** Dónde se ofrece una categoría: en el gasto de una casa o en el de la empresa. */
export const ambitoCategoriaSchema = z.enum(['PROPIEDAD', 'INTERNA']);
export const categoriaSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    ambito: ambitoCategoriaSchema,
    /** Las del sistema se renombran y se desactivan, pero no se borran. */
    esSistema: z.boolean(),
    activa: z.boolean(),
    orden: z.number().int(),
    /** Cuántos gastos la usan. Es lo que decide si se puede borrar. */
    gastos: z.number().int(),
});
export const crearCategoriaSchema = z.object({
    nombre: z.string().min(2).max(80),
    ambito: ambitoCategoriaSchema,
});
export const editarCategoriaSchema = z.object({
    nombre: z.string().min(2).max(80).optional(),
    activa: z.boolean().optional(),
});
/** Lo que el navegador entrega al suscribir un dispositivo a los avisos. */
export const suscripcionPushSchema = z.object({
    endpoint: z.string().url().max(600),
    clavep256dh: z.string().min(10).max(300),
    claveAuth: z.string().min(4).max(300),
    /** Como reconocer el dispositivo en la lista: «iPhone de Matias». */
    dispositivo: z.string().max(200).nullable().optional(),
});
/**
 * Un dispositivo suscrito, tal y como se le ensena a su dueno.
 *
 * `fallos` no es ruido tecnico: a partir de cinco se deja de intentar, y sin
 * verlo en pantalla un telefono se queda mudo para siempre sin que nadie sepa
 * por que.
 */
export const dispositivoPushSchema = z.object({
    dispositivo: z.string().nullable(),
    creadoEn: z.string(),
    usadoEn: z.string().nullable(),
    fallos: z.number().int(),
    /** El de quien pregunta, para distinguir «este» de «el otro». */
    esteDispositivo: z.boolean(),
});
/** Lo que de verdad paso al mandar la prueba a cada dispositivo. */
export const envioPruebaSchema = z.object({
    dispositivo: z.string().nullable(),
    entregado: z.boolean(),
    /** Lo que contesto el fabricante: 201 bien, 403 clave mal, 410 caducado. */
    codigo: z.number().int().nullable(),
    motivo: z.string().nullable(),
});
export const preferenciaAvisoSchema = z.object({
    tipo: tipoAvisoSchema,
    push: z.boolean(),
});
export const guardarPreferenciaAvisoSchema = z.object({
    tipo: tipoAvisoSchema,
    push: z.boolean(),
});
// --- Oportunidades de obra ---------------------------------------------------
/*
 * EL MODULO DEJA DE SER UNA SIMULACION.
 *
 * Hasta ahora el modelo vivia solo en el navegador (`apps/web/src/obras`) y el
 * estado se perdia al recargar. Aqui esta el contrato de verdad: es lo que
 * viaja entre la pantalla y el servidor, y lo que la base de datos guarda.
 *
 * Se separan DOS DIMENSIONES que se confunden al hablar de obras: como va la
 * CONSTRUCCION (`fase`, `progreso`) y como va la CONVERSACION (`etapa`). Una
 * obra terminada puede seguir siendo una oportunidad sin contactar.
 */
export const FASES_OBRA = [
    'PREPARACION_TERRENO', 'CIMENTACION', 'ESTRUCTURA', 'CERRAMIENTOS',
    'INSTALACIONES', 'ACABADOS_INTERIORES', 'EXTERIORES', 'PREPARACION_ENTREGA',
    'FINALIZADA', 'DESCONOCIDO',
];
export const faseObraSchema = z.enum(FASES_OBRA);
export const CONFIANZAS_OBRA = ['BAJA', 'MEDIA', 'ALTA'];
export const confianzaObraSchema = z.enum(CONFIANZAS_OBRA);
export const ETAPAS_OBRA = [
    'DETECTADA', 'INVESTIGANDO', 'PENDIENTE_CONTACTAR', 'CONTACTADA',
    'CONVERSACION', 'REUNION', 'PROPUESTA', 'GANADA', 'PERDIDA', 'PAUSA',
];
export const etapaObraSchema = z.enum(ETAPAS_OBRA);
export const TIPOS_PROYECTO_OBRA = ['VILLA', 'PROMOCION', 'APARTAMENTO', 'REFORMA', 'OTRO'];
export const tipoProyectoObraSchema = z.enum(TIPOS_PROYECTO_OBRA);
export const ROLES_CONTACTO_OBRA = [
    'PROPIETARIO', 'PROMOTOR', 'CONSTRUCTORA', 'ARQUITECTO', 'PROJECT_MANAGER',
    'COMERCIALIZADORA', 'AGENTE', 'OTRO',
];
export const rolContactoObraSchema = z.enum(ROLES_CONTACTO_OBRA);
export const INFLUENCIAS_OBRA = ['DECIDE', 'INFLUYE', 'INFORMA'];
export const influenciaObraSchema = z.enum(INFLUENCIAS_OBRA);
export const CANALES_OBRA = ['TELEFONO', 'EMAIL', 'WHATSAPP', 'PRESENCIAL'];
export const canalObraSchema = z.enum(CANALES_OBRA);
export const TIPOS_ACCION_OBRA = [
    'INVESTIGAR', 'LLAMAR', 'EMAIL', 'WHATSAPP', 'VISITAR', 'PRESENTACION',
    'REUNION', 'PROPUESTA', 'SEGUIMIENTO', 'VERIFICAR', 'OTRO',
];
export const tipoAccionObraSchema = z.enum(TIPOS_ACCION_OBRA);
export const PRIORIDADES_OBRA = ['ALTA', 'MEDIA', 'BAJA'];
export const prioridadObraSchema = z.enum(PRIORIDADES_OBRA);
export const TIPOS_APUNTE_OBRA = [
    'CREACION', 'ETAPA', 'PROGRESO', 'CONTACTO', 'TAREA', 'NOTA', 'LLAMADA',
    'EMAIL', 'WHATSAPP', 'VISITA', 'REUNION', 'CONVERSION',
];
export const tipoApunteObraSchema = z.enum(TIPOS_APUNTE_OBRA);
/**
 * Tres estados, no dos. `null` es «todavia no se sabe», que no es «no»: una
 * casilla de verificacion convierte una pregunta sin responder en una
 * afirmacion falsa.
 */
const ternarioSchema = z.boolean().nullable();
export const situacionObraSchema = z.object({
    propietarioConocido: ternarioSchema,
    usoDelPropietario: ternarioSchema,
    saldraAVenta: ternarioSchema,
    yaEnVenta: ternarioSchema,
});
export const contactoObraSchema = z.object({
    id: z.string().min(1).max(80),
    nombre: z.string().min(1).max(200),
    rol: rolContactoObraSchema,
    telefono: z.string().max(60).nullable(),
    email: z.string().max(200).nullable(),
    web: z.string().max(300).nullable(),
    idioma: z.string().max(40).nullable(),
    canal: canalObraSchema.nullable(),
    influencia: influenciaObraSchema.nullable(),
    notas: z.string().max(2000).nullable(),
    /** Cuando alguien ha pedido que no se le contacte, se respeta y se ve. */
    sinContacto: z.boolean(),
});
export const accionObraSchema = z.object({
    id: z.string().min(1).max(80),
    tipo: tipoAccionObraSchema,
    descripcion: z.string().max(1000),
    responsable: z.string().max(200),
    cuando: z.string().max(40),
    prioridad: prioridadObraSchema,
    recordatorio: z.boolean(),
    hecha: z.boolean(),
});
export const apunteObraSchema = z.object({
    id: z.string().min(1).max(80),
    tipo: tipoApunteObraSchema,
    cuando: z.string().max(40),
    quien: z.string().max(200),
    texto: z.string().max(4000),
});
export const obraSchema = z.object({
    id: z.string().min(1).max(80),
    codigo: z.string().max(40),
    nombre: z.string().max(200),
    direccion: z.string().max(300),
    codigoPostal: z.string().max(20),
    municipio: z.string().max(120),
    zona: z.string().max(120),
    lat: z.number(),
    lon: z.number(),
    tipoProyecto: tipoProyectoObraSchema,
    fase: faseObraSchema,
    progreso: z.number().int().min(0).max(100),
    finalizacionEstimada: z.string().max(20).nullable(),
    confianza: confianzaObraSchema,
    ultimaComprobacion: z.string().max(20).nullable(),
    comprobadaPor: z.string().max(200).nullable(),
    comentarioObra: z.string().max(4000).nullable(),
    etapa: etapaObraSchema,
    responsable: z.string().max(200).nullable(),
    valorMercadoCents: z.number().int().nullable(),
    potencialCents: z.number().int().nullable(),
    origen: z.string().max(200).nullable(),
    etiquetas: z.array(z.string().max(60)).max(40),
    motivoPerdida: z.string().max(2000).nullable(),
    situacion: situacionObraSchema,
    contactos: z.array(contactoObraSchema).max(50),
    acciones: z.array(accionObraSchema).max(200),
    historial: z.array(apunteObraSchema).max(500),
    /** Queda cuando se convierte, para no perder de donde vino el cliente. */
    convertidaEn: z
        .object({ clienteId: z.string(), propiedadId: z.string(), cuando: z.string() })
        .nullable(),
});
// --- La ficha de obra, leida de una foto -------------------------------------
/*
 * LA RUTINA DIARIA.
 *
 * Quien va a ver las obras rellena una ficha de papel: direccion, en que fase
 * esta, quien construye, un telefono si lo ha conseguido. Volver y teclear
 * treinta fichas es el trabajo que nadie hace, y por eso las fichas se quedan
 * en la carpeta.
 *
 * Se sube la FOTO del papel y se lee. Lo que sale es un BORRADOR que alguien
 * revisa antes de guardar: nunca se da de alta sola. Por eso viaja con lo que
 * el lector no ha entendido —`dudas`— y con los campos que si ha sacado:
 * revisar cuatro casillas es rapido; adivinar cuales miran, no.
 */
export const leerFichaObraSchema = z.object({
    nombreFichero: z.string().min(1).max(300),
    tipoMime: z.enum(['image/jpeg', 'image/png', 'image/webp', 'image/gif']),
    /** El fichero en base64, sin el prefijo `data:`. */
    contenidoBase64: z.string().min(1),
});
export const lecturaFichaObraSchema = z.object({
    /** El borrador, con los valores por defecto donde la foto no decia nada. */
    obra: obraSchema,
    /** Que campos vienen de la foto. Lo demas esta en blanco, no leido. */
    camposLeidos: z.array(z.string()).max(60),
    /** Lo que el lector no ha sabido interpretar, en una linea cada cosa. */
    dudas: z.array(z.string().max(300)).max(30),
});
// --- Corregir y quitar cargas de horas y de gastos ---------------------------
/*
 * SE CARGA MAL Y NO HABIA FORMA DE ARREGLARLO.
 *
 * Hasta ahora una hora o un gasto solo se podian crear. Un importe tecleado con
 * un cero de mas, un gasto puesto en la casa equivocada o una carga duplicada se
 * quedaban dentro para siempre, contando en el resumen del mes y en lo que se le
 * factura al cliente. La unica salida era no mirar los numeros.
 *
 * Todo lo que se manda es OPCIONAL: lo que no viene, no se toca. Asi corregir la
 * fecha de una carga no exige reenviar el concepto, el importe y el margen, que
 * es como se pisan datos sin querer.
 */
export const editarHorasSchema = z.object({
    userId: z.string().uuid().optional(),
    propertyId: z.string().uuid().nullable().optional(),
    fecha: fechaISO.optional(),
    minutos: z.number().int().min(1).max(MAX_MINUTOS_CARGA).optional(),
    tipo: tipoHoraSchema.optional(),
    descripcion: z.string().max(2000).nullable().optional(),
    costeHoraCents: z.number().int().min(0).optional(),
    precioHoraClienteCents: z.number().int().min(0).optional(),
    facturableExtra: z.boolean().optional(),
    incidencias: z.array(z.string().uuid()).max(30).optional(),
});
export const editarGastoSchema = z.object({
    propertyId: z.string().uuid().nullable().optional(),
    fecha: fechaISO.optional(),
    concepto: z.string().min(2).max(300).optional(),
    categoria: categoriaGastoSchema.optional(),
    costeCents: z.number().int().min(0).optional(),
    moneda: z.string().length(3).optional(),
    margenPb: z.number().int().min(0).max(1_000_000).optional(),
    facturableCliente: z.boolean().optional(),
    visibleCliente: z.boolean().optional(),
    providerId: z.string().uuid().nullable().optional(),
    beneficiario: z.string().max(200).nullable().optional(),
    descripcion: z.string().max(2000).nullable().optional(),
    incidencias: z.array(z.string().uuid()).max(30).optional(),
});
/** De que se esta hablando en Interno: lo del equipo, lo de las casas, o todo. */
export const ambitoCargaSchema = z.enum(['INTERNO', 'PROPIEDADES', 'TODO']);
/**
 * Quien puede encargarse de una incidencia de una casa.
 *
 * Se ensena a todo el equipo, no solo a quien puede: si Manuel no aparece en
 * la lista nadie sabe por que. Aparece, y si no tiene esa casa asignada sale
 * marcado y no se puede elegir —asignarle algo que no puede abrir seria darle
 * una tarea que no puede hacer—.
 */
export const responsablePosibleSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    rol: rolSchema,
    conAcceso: z.boolean(),
});
// --- Puerta a puerta ---------------------------------------------------------
/*
  LA LIBRETA DEL CAPTADOR.

  Un captador recorre una calle, llama a treinta timbres y vuelve con la
  informacion en la cabeza. A la semana siguiente nadie sabe que portales se
  hicieron, en cuales abrieron ni que contestaron, y la calle se recorre entera
  otra vez. Esto es una fila por puerta.
*/
export const ESTADOS_PUERTA = [
    'ATENDIO',
    'NO_ATENDIO',
    'SEGUIMIENTO',
    'DESCARTADA',
    'CLIENTE',
];
export const estadoPuertaSchema = z.enum(ESTADOS_PUERTA);
/**
 * Lo que contestan en la puerta, de la lista corta.
 *
 * Son las ocho respuestas que se repiten; se eligen de un toque en vez de
 * escribirlas. `OTRA` abre el campo libre, porque la novena siempre existe.
 */
export const RESPUESTAS_PUERTA = [
    'YA_TIENE_GESTOR',
    'NO_INTERESA',
    'DEJA_INFORMACION',
    'LLAMA_MAS_ADELANTE',
    'SEGUNDA_RESIDENCIA',
    'NO_ES_PROPIETARIO',
    'QUIERE_INFORMACION',
    'OTRA',
];
export const respuestaPuertaSchema = z.enum(RESPUESTAS_PUERTA);
export const MATERIALES_PUERTA = ['FLYER', 'TARJETA', 'AMBOS', 'NINGUNO'];
export const materialPuertaSchema = z.enum(MATERIALES_PUERTA);
/** Un seguimiento o una tarea sobre una puerta. */
export const accionPuertaSchema = z.object({
    id: z.string(),
    texto: z.string().max(600),
    /** Cuando toca volver. Vacio = sin fecha, es solo una nota. */
    fecha: z.string().nullable(),
    hecha: z.boolean(),
    creadaPor: z.string().nullable(),
    creadaEn: z.string(),
});
/** Una linea del historial: lo que paso y cuando. */
export const apuntePuertaSchema = z.object({
    id: z.string(),
    cuando: z.string(),
    quien: z.string().nullable(),
    texto: z.string().max(600),
});
export const puertaSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    direccion: z.string().max(300),
    codigoPostal: z.string().max(20),
    municipio: z.string().max(120),
    zona: z.string().max(120),
    lat: z.number(),
    lon: z.number(),
    estado: estadoPuertaSchema,
    respuesta: respuestaPuertaSchema.nullable(),
    respuestaTexto: z.string().max(600).nullable(),
    material: materialPuertaSchema,
    contactoNombre: z.string().max(200).nullable(),
    contactoTelefono: z.string().max(60).nullable(),
    notas: z.string().max(2000).nullable(),
    fotoPortada: z.string().uuid().nullable(),
    fotos: z.array(adjuntoBreveSchema),
    acciones: z.array(accionPuertaSchema),
    historial: z.array(apuntePuertaSchema),
    visitadaEn: z.string(),
    registradaPor: z.string().nullable(),
    creadoEn: z.string(),
    actualizadoEn: z.string(),
});
/**
 * LO QUE SE MANDA DESDE LA CALLE.
 *
 * Tiene que caber en menos de cuarenta segundos con el movil en una mano, asi
 * que casi todo es opcional: la unica obligacion es saber DONDE. Una puerta sin
 * direccion no sirve para nada; una sin nombre de contacto, si.
 */
export const crearPuertaSchema = z.object({
    direccion: z.string().min(3).max(300),
    codigoPostal: z.string().max(20).optional(),
    municipio: z.string().max(120).optional(),
    zona: z.string().max(120).optional(),
    lat: z.number().optional(),
    lon: z.number().optional(),
    atendio: z.boolean(),
    respuesta: respuestaPuertaSchema.optional(),
    respuestaTexto: z.string().max(600).optional(),
    material: materialPuertaSchema.optional(),
    contactoNombre: z.string().max(200).optional(),
    contactoTelefono: z.string().max(60).optional(),
    notas: z.string().max(2000).optional(),
});
/** La ficha se edita entera, como la de una obra. */
export const editarPuertaSchema = puertaSchema.omit({
    id: true,
    codigo: true,
    fotos: true,
    registradaPor: true,
    creadoEn: true,
    actualizadoEn: true,
});
//# sourceMappingURL=index.js.map